import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { getCohorts } from "apis";
import {
  getCampaignQuestions as getCampaignQuestionsRequest,
  getCampaigns as getCampaignsRequest,
  getQuestionMediaUploadUrl as getQuestionMediaUploadUrlRequest,
  saveCampaignQuestions as saveCampaignQuestionsRequest,
  uploadQuestionMediaToS3 as uploadQuestionMediaToS3Request,
} from "apis/campaign.api";
import { getQuestionBank as getQuestionBankRequest } from "apis/question-bank.api";
import { Path } from "enums";
import styles from "./survey-builder.page.module.scss";

const SURVEY_GUIDE_STORAGE_PREFIX = "survey-guide-draft:";
const ROUTE_END = "__END__";
const ROUTE_CONTINUE = "__CONTINUE__";
const SURVEY_GUIDE_DOCUMENT_VERSION = 1;
const ANCHOR_LOCK_HINT = "Copy locked · routing open";
const ANCHOR_DELETE_BLOCK_REASON = "Anchor questions are required in every guide.";

const STANDARD_ANCHOR_QUESTIONS = [
  {
    key: "gender",
    code: "__gender__",
    label: "Gender",
    section: "Anchor Questions",
    prompt: "Gender of respondent",
    helperText: "Itala ang kasarian ng respondent.",
    answerType: "multipleChoice",
    options: ["Male", "Female"],
    defaultRoutes: [ROUTE_CONTINUE, ROUTE_CONTINUE],
    demographicField: "sex",
    overrideTranscription: true,
  },
  {
    key: "age_band",
    code: "__age__",
    label: "Age Band",
    section: "Anchor Questions",
    prompt: "Ilang taon na po kayo?",
    helperText: "Piliin ang age group ng respondent.",
    answerType: "multipleChoice",
    options: ["17 and below", "18-29", "30-44", "45-59", "60+"],
    defaultRoutes: [ROUTE_CONTINUE, ROUTE_CONTINUE, ROUTE_CONTINUE, ROUTE_CONTINUE, ROUTE_CONTINUE],
    demographicField: "ageBand",
    overrideTranscription: true,
  },
  {
    key: "data_privacy_consent",
    code: "__privacy__",
    label: "Data Privacy Consent",
    section: "Anchor Questions",
    prompt: "Okay lang po ba na i-record natin ang usapan? Protektado po kayo ng Data Privacy Act.",
    helperText: "Kailangan ng pahintulot bago magpatuloy.",
    answerType: "multipleChoice",
    options: ["OO — mag-record", "Hindi - I-end ang Interview."],
    defaultRoutes: [ROUTE_CONTINUE, ROUTE_END],
    demographicField: null,
    overrideTranscription: false,
  },
];

const LEGACY_ANCHOR_MATCHERS = {
  gender: [/\bgender\b/i, /kasarian/i, /sex\b/i],
  age_band: [/ilang taon/i, /age group/i, /17 and below/i, /18-29/i, /30-44/i, /45-59/i, /60\+/i],
  data_privacy_consent: [/data privacy/i, /i-?record/i, /pahintulot/i, /consent/i],
};

const STANDARD_PRELIMINARY_QUESTIONS = [
  {
    key: "gender_check",
    label: "Gender Check",
    prompt: "Gender of respondent",
    demographicField: "sex",
    overrideTranscription: true,
  },
  {
    key: "age_check",
    label: "Age Check",
    prompt: "Ilang taon na po kayo?",
    demographicField: "ageBand",
    overrideTranscription: true,
  },
  {
    key: "data_privacy_consent",
    label: "Data Privacy Consent",
    prompt: "Data Privacy consent to record",
    demographicField: null,
    overrideTranscription: false,
  },
];

const ANSWER_TYPES = [
  { value: "shortText", label: "Short Field", supportsOptions: false },
  { value: "multipleChoice", label: "Multiple Choice", supportsOptions: true },
  { value: "ranking", label: "Ranking", supportsOptions: true },
];

const GUIDE_CLOSING_SPOKEN_LINE =
  "\u201cMaraming salamat po sa oras at sa mga sagot niyo! Magandang araw po.\u201d";
const GUIDE_WRAP_UP_REMINDER = "I-save at i-submit ang interview.";
const GUIDE_FINAL_NOTE =
  "Thank the respondent. STOP the recording app after the final question \u2192 SYNC \u2192 SUBMIT the interview.";

const MEDIA_FILE_ACCEPT = ".jpg,.jpeg,.png,.gif,.mp4,.mov,.avi";
const MEDIA_ALLOWED_CONTENT_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "video/mp4",
  "video/quicktime",
  "video/x-msvideo",
]);
const MEDIA_EXTENSION_TO_CONTENT_TYPE = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  mp4: "video/mp4",
  mov: "video/quicktime",
  avi: "video/x-msvideo",
};

const MEDIA_ERROR_MESSAGES = {
  CP1019: "Unsupported file type. Use jpg, jpeg, png, gif, mp4, mov, or avi.",
  CP1020: "Attachment key is invalid. Reattach the file and try saving again.",
  CP1021: "Attachment upload is incomplete or mismatched. Please reupload the file.",
  CP1022: "Uploaded file type is not allowed. Please upload a supported format.",
  CP1023: "Uploaded file exceeds size limits.",
  CP1024: "Media storage is not configured in the backend. Please contact support.",
};

const QUESTION_BANK_SAVE_ERRORS = {
  CP1037: "This Question Bank item no longer exists. Remove it and add it again.",
  CP1038:
    "This canned question no longer matches the Question Bank. Reload the survey guide before saving.",
  CP1039: "A canned question appears more than once. Remove the duplicate before saving.",
  CP1040: "Media attachments are not allowed on canned questions.",
  CP1041: "The Question Bank identity changed. Remove this card and add it again.",
};

const MAP_DIMENSIONS = {
  nodeWidth: 200,
  nodeHeight: 58,
  endWidth: 164,
  endHeight: 32,
  laneGap: 232,
  rowGap: 124,
  paddingX: 88,
  paddingY: 42,
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const formatCampaignDate = (value) => {
  if (!value) return "-";
  const asString = String(value);
  return asString.includes("T") ? asString.split("T")[0] : asString;
};

const normalizeCampaign = (campaign) => {
  if (!campaign) return null;

  const code = campaign?.campaignCode || campaign?.code || "";
  const rawCampaignType = String(campaign?.campaignType || "").toLowerCase();
  const rawSurveyType = String(campaign?.surveyType || campaign?.type || "single").toLowerCase();
  const surveyType =
    rawCampaignType === "custom_survey" || rawSurveyType === "custom" ? "custom" : "single";

  return {
    campaignId: campaign?.campaignId,
    code,
    title: campaign?.description || campaign?.name || code,
    date: formatCampaignDate(campaign?.campaignDate || campaign?.date),
    location: campaign?.location || "Unknown",
    surveyType,
    surveyQuestionDocument: campaign?.surveyQuestionDocument || "",
  };
};

const mergeCampaignFromRoute = (campaign, routedCampaign) => {
  if (!campaign) return campaign;

  const campaignCode = String(campaign?.code || "").toLowerCase();
  const routedCode = String(routedCampaign?.code || "").toLowerCase();

  if (!routedCode || campaignCode !== routedCode) return campaign;

  return {
    ...campaign,
    surveyType:
      routedCampaign?.surveyType === "custom" ? routedCampaign.surveyType : campaign.surveyType,
    title: routedCampaign?.title || campaign.title,
  };
};

const createQuestionId = () => `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const createOptionId = () => `opt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const createNoteId = () => `note-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createQuestionKey = (questionId) =>
  `q_${String(questionId || createQuestionId()).replace(/-/g, "_")}`;

const ensureUniqueQuestionKeys = (questions) => {
  const usedKeys = new Set();

  return questions.map((question) => {
    const originalKey = String(question?.code || "").trim();
    let key = originalKey || createQuestionKey(question?.id);

    if (usedKeys.has(key.toLowerCase())) {
      key = createQuestionKey(question?.id);
    }

    while (usedKeys.has(key.toLowerCase())) {
      key = createQuestionKey();
    }

    usedKeys.add(key.toLowerCase());
    return { ...question, code: key };
  });
};

const getAnswerTypeMeta = (value) =>
  ANSWER_TYPES.find((entry) => entry.value === value) || ANSWER_TYPES[0];

const isAnchorQuestion = (question) => Boolean(question?.isAnchor);
const isCannedQuestion = (question) => Boolean(question?.bankQuestionId);

const getAnchorId = (anchorKey) => `anchor:${anchorKey}`;

const createAnchorOption = (anchorKey, optionLabel, optionIndex, existingOption) => ({
  id: existingOption?.id || `anchor-opt:${anchorKey}:${optionIndex + 1}`,
  campaignQuestionOptionId: normalizeEntityId(existingOption?.campaignQuestionOptionId),
  label: optionLabel,
  routeTo: existingOption?.routeTo,
  cohorts: Array.isArray(existingOption?.cohorts) ? existingOption.cohorts : [],
});

const createAnchorQuestion = (definition, existingQuestion) => {
  const existingOptions = Array.isArray(existingQuestion?.options) ? existingQuestion.options : [];
  const canonicalOptions = definition.options.map((optionLabel, optionIndex) => {
    const option = createAnchorOption(
      definition.key,
      optionLabel,
      optionIndex,
      existingOptions[optionIndex],
    );
    return {
      ...option,
      routeTo: option.routeTo || definition.defaultRoutes[optionIndex] || ROUTE_CONTINUE,
    };
  });

  const helperNoteText = definition.helperText || "";
  const helperNote = helperNoteText
    ? [
        {
          id: existingQuestion?.interviewerNotes?.[0]?.id || createNoteId(),
          text: helperNoteText,
        },
      ]
    : [];

  return {
    id: existingQuestion?.id || getAnchorId(definition.key),
    campaignQuestionId: normalizeEntityId(existingQuestion?.campaignQuestionId),
    code: definition.code,
    section: definition.section,
    prompt: definition.prompt,
    answerType: definition.answerType,
    interviewerNotes: helperNote,
    options: canonicalOptions,
    routeTo: ROUTE_CONTINUE,
    mediaType: null,
    mediaKey: null,
    mediaUrl: null,
    mediaName: "",
    mediaUploading: false,
    mediaUploadError: null,
    expanded: existingQuestion?.expanded !== false,
    isAnchor: true,
    anchorKey: definition.key,
    lockHint: ANCHOR_LOCK_HINT,
  };
};

const detectLegacyAnchorKey = (question) => {
  const code = String(question?.code || "")
    .toLowerCase()
    .trim();
  const explicitAnchorKey = String(question?.anchorKey || "")
    .toLowerCase()
    .trim();

  const byCode = STANDARD_ANCHOR_QUESTIONS.find((entry) =>
    [entry.code.toLowerCase(), getAnchorId(entry.key).toLowerCase()].includes(code),
  );
  if (byCode) return byCode.key;

  const byKey = STANDARD_ANCHOR_QUESTIONS.find((entry) => entry.key === explicitAnchorKey);
  if (byKey) return byKey.key;

  const prompt = String(question?.prompt || question?.title || "").toLowerCase();

  return (
    STANDARD_ANCHOR_QUESTIONS.find((entry) =>
      (LEGACY_ANCHOR_MATCHERS[entry.key] || []).some((matcher) => matcher.test(prompt)),
    )?.key || null
  );
};

const applyAnchorQuestions = (questions, startQuestionId, { retireLegacy = false } = {}) => {
  const normalized = Array.isArray(questions) ? questions : [];
  const routeRemap = new Map();
  const notices = [];

  const anchorByKey = new Map();
  const orderedQuestions = [];

  normalized.forEach((question) => {
    const anchorKey = detectLegacyAnchorKey(question);
    if (!anchorKey) {
      orderedQuestions.push(question);
      return;
    }

    const definition = STANDARD_ANCHOR_QUESTIONS.find((entry) => entry.key === anchorKey);
    if (!definition) {
      orderedQuestions.push(question);
      return;
    }

    if (!anchorByKey.has(anchorKey)) {
      const canonicalAnchor = createAnchorQuestion(definition, question);
      anchorByKey.set(anchorKey, canonicalAnchor);
      orderedQuestions.push(canonicalAnchor);
      return;
    }

    if (retireLegacy) {
      const keptAnchor = anchorByKey.get(anchorKey);
      routeRemap.set(question.id, keptAnchor.id);
      notices.push(
        `Retired duplicate ${definition.label} question and repointed incoming routes to ${definition.code}.`,
      );
    } else {
      orderedQuestions.push(question);
    }
  });

  const missingAnchors = [];

  STANDARD_ANCHOR_QUESTIONS.forEach((definition) => {
    if (anchorByKey.has(definition.key)) return;
    const injectedAnchor = createAnchorQuestion(definition);
    anchorByKey.set(definition.key, injectedAnchor);
    missingAnchors.push(injectedAnchor);
  });

  const retainedQuestions = [...missingAnchors, ...orderedQuestions].filter(
    (question) => !routeRemap.has(question.id),
  );

  const repointed = retainedQuestions.map((question) => {
    const hasOptionRouting = usesOptionRouting(question.answerType);

    if (hasOptionRouting) {
      return {
        ...question,
        options: (question.options || []).map((option, optionIndex) => {
          const fallbackRoute =
            question.isAnchor && detectLegacyAnchorKey(question)
              ? STANDARD_ANCHOR_QUESTIONS.find((entry) => entry.key === question.anchorKey)
                  ?.defaultRoutes?.[optionIndex] || ROUTE_CONTINUE
              : option.routeTo;
          const nextRoute = routeRemap.get(option.routeTo) || option.routeTo || fallbackRoute;
          return {
            ...option,
            routeTo: nextRoute,
          };
        }),
      };
    }

    const nextRoute = routeRemap.get(question.routeTo) || question.routeTo;
    return {
      ...question,
      routeTo: nextRoute,
    };
  });

  const finalQuestions = repointed.map((question) => {
    const anchorKey = detectLegacyAnchorKey(question);
    if (!anchorKey)
      return { ...question, isAnchor: false, anchorKey: undefined, lockHint: undefined };

    const definition = STANDARD_ANCHOR_QUESTIONS.find((entry) => entry.key === anchorKey);
    return createAnchorQuestion(definition, { ...question, anchorKey });
  });

  const remappedStartQuestionId = routeRemap.get(startQuestionId) || startQuestionId;

  return {
    questions: finalQuestions,
    startQuestionId: remappedStartQuestionId,
    notices,
  };
};

const getQuestionCodeLabel = (question, index) =>
  String(question?.code || `q${index + 1}`).toUpperCase();

const getMediaFilenameFromKey = (mediaKey) => {
  if (!mediaKey) return "";
  const pathChunks = String(mediaKey).split("/");
  return pathChunks[pathChunks.length - 1] || "";
};

const getFileExtension = (filename) => {
  const value = String(filename || "")
    .trim()
    .toLowerCase();
  const dotIndex = value.lastIndexOf(".");
  if (dotIndex < 0 || dotIndex === value.length - 1) return "";
  return value.slice(dotIndex + 1);
};

const normalizeMediaType = (mediaType) => {
  const normalized = String(mediaType || "").toLowerCase();
  return normalized === "image" || normalized === "video" ? normalized : null;
};

const getFirstNonEmptyString = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const normalized = String(value).trim();
    if (normalized) return normalized;
  }

  return null;
};

const resolveMediaUrl = (question) =>
  getFirstNonEmptyString(
    question?.mediaUrl,
    question?.mediaURL,
    question?.mediaSignedUrl,
    question?.signedMediaUrl,
    question?.mediaPreviewUrl,
    question?.previewUrl,
    question?.media_url,
    question?.media_signed_url,
    question?.media_preview_url,
    question?.preview_url,
    question?.media?.url,
    question?.media?.signedUrl,
    question?.media?.signed_url,
  );

const inferMediaTypeFromContent = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (!normalized) return null;

  if (normalized.includes("image/")) return "image";
  if (normalized.includes("video/")) return "video";

  const extension = getFileExtension(normalized);
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) return "image";
  if (["mp4", "mov", "avi", "m4v", "webm"].includes(extension)) return "video";

  return null;
};

const resolveMediaType = (question) => {
  const explicitType = normalizeMediaType(
    getFirstNonEmptyString(question?.mediaType, question?.media_type, question?.type),
  );
  if (explicitType) return explicitType;

  const fromContentType = inferMediaTypeFromContent(
    getFirstNonEmptyString(
      question?.mediaContentType,
      question?.media_content_type,
      question?.contentType,
      question?.content_type,
    ),
  );
  if (fromContentType) return fromContentType;

  const fromKey = inferMediaTypeFromContent(
    getFirstNonEmptyString(question?.mediaKey, question?.media_key),
  );
  if (fromKey) return fromKey;

  return inferMediaTypeFromContent(resolveMediaUrl(question));
};

const normalizeMediaContentType = (value) => {
  const normalized = String(value || "").toLowerCase();

  if (normalized === "image/jpg" || normalized === "image/pjpeg") {
    return "image/jpeg";
  }

  if (normalized === "video/avi") {
    return "video/x-msvideo";
  }

  return normalized;
};

const resolveUploadContentType = (file) => {
  const extension = getFileExtension(file?.name);
  const fallbackType = MEDIA_EXTENSION_TO_CONTENT_TYPE[extension] || null;
  const normalizedFileType = normalizeMediaContentType(file?.type);

  if (MEDIA_ALLOWED_CONTENT_TYPES.has(normalizedFileType)) {
    return {
      contentType: normalizedFileType,
      extension,
    };
  }

  if (fallbackType) {
    return {
      contentType: fallbackType,
      extension,
    };
  }

  return {
    contentType: null,
    extension,
  };
};

const getFirstSupportedMediaFile = (fileList) => {
  const files = Array.from(fileList || []);
  return files.find((file) => Boolean(resolveUploadContentType(file).contentType)) || null;
};

const normalizeEntityId = (value) => {
  if (value == null || value === "") return null;
  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : null;
};

const normalizeMediaKey = (value) => {
  if (value === null) return null;
  if (value == null) return undefined;
  const normalized = String(value).trim();
  return normalized || undefined;
};

const getMediaErrorMessage = (error, fallback) => {
  const errorCode =
    error?.code || error?.response?.data?.code || error?.data?.code || error?.statusCode || "";

  if (errorCode && QUESTION_BANK_SAVE_ERRORS[errorCode]) {
    return QUESTION_BANK_SAVE_ERRORS[errorCode];
  }

  if (errorCode && MEDIA_ERROR_MESSAGES[errorCode]) {
    return MEDIA_ERROR_MESSAGES[errorCode];
  }

  return error?.response?.data?.message || error?.data?.message || error?.message || fallback;
};

const truncateText = (value, maxLength = 88) => {
  const normalized = String(value || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return "Untitled question";
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 1)}…` : normalized;
};

const escapeHtml = (value) =>
  String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const sanitizeFilenamePart = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9-_]/g, "")
    .slice(0, 60);

const isRankingAnswerType = (answerType) => {
  const normalized = String(answerType || "")
    .toLowerCase()
    .trim();
  return normalized === "ranking" || normalized === "rank" || normalized === "ranked_choice";
};

const usesOptionRouting = (answerType) =>
  getAnswerTypeMeta(answerType).supportsOptions && !isRankingAnswerType(answerType);

const getRankingItemCount = (question) =>
  Math.max(1, Array.isArray(question?.options) ? question.options.length : 0);

const getResolvedRankCount = (question, optionsOverride) => {
  const optionCount = Math.max(1, Array.isArray(optionsOverride) ? optionsOverride.length : 0);
  const fallback = optionCount;
  const parsed = Number.parseInt(String(question?.rankCount ?? "").replace(/\D/g, ""), 10);

  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return clamp(parsed, 1, optionCount);
};

const getGuideAnswerTypeLabel = (question) => {
  if (isRankingAnswerType(question?.answerType)) return "Ranking";
  return getAnswerTypeMeta(question?.answerType).supportsOptions
    ? "Multiple choice"
    : "Short field";
};

const getEffectiveRouteTarget = (routeTo, fallbackRoute) =>
  !routeTo || routeTo === ROUTE_CONTINUE ? fallbackRoute : routeTo;

const buildFlowOrderQuestionIds = (questions, startQuestionId) => {
  if (!Array.isArray(questions) || !questions.length) return [];

  const questionById = new Map(questions.map((question) => [question.id, question]));
  const indexById = new Map(questions.map((question, index) => [question.id, index]));
  const visited = new Set();
  const order = [];
  const pendingSeeds = [];
  const seedSet = new Set();

  const enqueueSeed = (questionId) => {
    if (!questionId || questionId === ROUTE_END || !questionById.has(questionId)) return;
    if (visited.has(questionId) || seedSet.has(questionId)) return;
    pendingSeeds.push(questionId);
    seedSet.add(questionId);
  };

  const walkSequentialRun = (seedId) => {
    let currentId = seedId;

    while (
      currentId &&
      currentId !== ROUTE_END &&
      questionById.has(currentId) &&
      !visited.has(currentId)
    ) {
      visited.add(currentId);
      order.push(currentId);

      const question = questionById.get(currentId);
      const questionIndex = indexById.get(currentId);
      const fallbackRoute = getSequentialRouteTarget(questions, questionIndex);
      const hasOptionRouting = usesOptionRouting(question.answerType);

      if (hasOptionRouting) {
        (question.options || []).forEach((option) => {
          const target = getEffectiveRouteTarget(option.routeTo, fallbackRoute);
          if (target !== fallbackRoute && target !== ROUTE_END) {
            enqueueSeed(target);
          }
        });
      } else {
        const target = getEffectiveRouteTarget(question.routeTo, fallbackRoute);
        if (target !== fallbackRoute && target !== ROUTE_END) {
          enqueueSeed(target);
        }
      }

      if (
        !fallbackRoute ||
        fallbackRoute === ROUTE_END ||
        !questionById.has(fallbackRoute) ||
        visited.has(fallbackRoute)
      ) {
        break;
      }

      currentId = fallbackRoute;
    }
  };

  const preferredStart =
    startQuestionId && questionById.has(startQuestionId) ? startQuestionId : questions[0].id;

  enqueueSeed(preferredStart);

  while (pendingSeeds.length) {
    const nextSeed = pendingSeeds.shift();
    seedSet.delete(nextSeed);
    walkSequentialRun(nextSeed);
  }

  questions.forEach((question) => {
    if (!visited.has(question.id)) {
      walkSequentialRun(question.id);
    }
  });

  return order;
};

const chunkIntoPairs = (items) => {
  const safeItems = Array.isArray(items) ? items : [];
  const rows = [];

  for (let index = 0; index < safeItems.length; index += 2) {
    rows.push([safeItems[index], safeItems[index + 1] || null]);
  }

  return rows;
};

const getOptionCohortTag = (option) => {
  const names = (option?.cohorts || [])
    .map((cohort) => String(cohort?.name || "").trim())
    .filter(Boolean);

  return names.length ? ` [Cohort: ${names.join(", ")}]` : "";
};

const buildSurveyGuideDocumentHtml = (normalizedGuide, campaign) => {
  const questions = Array.isArray(normalizedGuide?.questions) ? normalizedGuide.questions : [];
  const flowOrderIds = buildFlowOrderQuestionIds(questions, normalizedGuide?.startQuestionId);
  const questionById = new Map(questions.map((question) => [question.id, question]));
  const questionIndexById = new Map(questions.map((question, index) => [question.id, index]));
  const questionNumberById = new Map(flowOrderIds.map((id, index) => [id, index + 1]));
  const routeTargetLabel = (targetId) => {
    if (targetId === ROUTE_END) return "end the interview";
    const number = questionNumberById.get(targetId);
    return number ? `go to Q${number}` : "";
  };

  const resolveQuestionMediaName = (question) =>
    getFirstNonEmptyString(
      question?.mediaName,
      question?.mediaFilename,
      question?.mediaFileName,
      getMediaFilenameFromKey(question?.mediaKey),
    ) || "attached media";

  const decisionEntries = [];
  const questionBlocks = [];
  let previousSection = null;

  flowOrderIds.forEach((questionId) => {
    const question = questionById.get(questionId);
    if (!question) return;

    const index = questionIndexById.get(question.id);
    const fallbackRoute = getSequentialRouteTarget(questions, index);
    const questionNumber = questionNumberById.get(question.id);
    const questionLabel = `Q${questionNumber}`;
    const section = String(question?.section || "Untitled Section").trim() || "Untitled Section";

    if (section !== previousSection) {
      questionBlocks.push(`<h2 class="section">${escapeHtml(section)}</h2>`);
      previousSection = section;
    }

    const answerTypeLabel = getGuideAnswerTypeLabel(question);
    const chips = [`<span class="chip">${escapeHtml(answerTypeLabel)}</span>`];
    if (question?.isAnchor) {
      chips.push('<span class="chip chip-anchor">Anchor</span>');
    }

    const mediaKind = question?.mediaType === "video" ? "video" : "image";
    const mediaName = resolveQuestionMediaName(question);
    const mediaCue =
      question?.mediaType || question?.mediaKey || question?.mediaUrl
        ? `<div class="media-cue">\u25b6 Show the ${escapeHtml(mediaKind)} in the app (${escapeHtml(
            mediaName,
          )}) before the respondent answers.</div>`
        : "";

    let answerBody = "";

    if (isRankingAnswerType(question.answerType)) {
      const rankingItems = (question.options || [])
        .map(
          (option, optionIndex) =>
            `<li>${escapeHtml(option?.label || `Item ${optionIndex + 1}`)}${escapeHtml(
              getOptionCohortTag(option),
            )}</li>`,
        )
        .join("");

      const slotCount = getResolvedRankCount(question, question.options);
      const rankingSlots = Array.from({ length: slotCount }, (_, slotIndex) => {
        return `<li><span class="slot-index">${slotIndex + 1}.</span><span class="slot-line"></span></li>`;
      }).join("");

      answerBody = `
        <div class="answer-title">Ranking items</div>
        <ol class="ranking-items">${rankingItems}</ol>
        <div class="answer-title">Rank slots</div>
        <ol class="rank-slots">${rankingSlots}</ol>
      `;
    } else if (getAnswerTypeMeta(question.answerType).supportsOptions) {
      const optionRows = chunkIntoPairs(question.options || [])
        .map((row) => {
          const columns = row
            .map((option) => {
              if (!option) return '<td class="option-cell option-cell-empty"></td>';

              const targetId = option.needsRoute
                ? null
                : getEffectiveRouteTarget(option.routeTo, fallbackRoute);
              const annotation = option.needsRoute
                ? "Route not set"
                : targetId === fallbackRoute
                  ? ""
                  : routeTargetLabel(targetId);
              if (annotation && !option.needsRoute) {
                decisionEntries.push({
                  questionLabel,
                  questionPrompt: question.prompt,
                  optionLabel: option.label,
                  annotation,
                });
              }

              return `
                <td class="option-cell">
                  <span class="checkbox" aria-hidden="true"></span>
                  <span class="option-text">${escapeHtml(option?.label || "")}${escapeHtml(
                    getOptionCohortTag(option),
                  )}${annotation ? `<span class="branch-note"> &nbsp;\u2192 ${escapeHtml(annotation)}</span>` : ""}</span>
                </td>
              `;
            })
            .join("");

          return `<tr>${columns}</tr>`;
        })
        .join("");

      answerBody = `<table class="option-grid" role="presentation"><tbody>${optionRows}</tbody></table>`;
    } else {
      const directTarget = question.needsRoute
        ? null
        : getEffectiveRouteTarget(question.routeTo, fallbackRoute);
      if (!question.needsRoute && directTarget !== fallbackRoute) {
        decisionEntries.push({
          questionLabel,
          questionPrompt: question.prompt,
          optionLabel: "Direct response",
          annotation: question.needsRoute ? "Route not set" : routeTargetLabel(directTarget),
        });
      }

      answerBody = `
        <div class="ruled-lines">
          <div class="ruled-line"></div>
          <div class="ruled-line"></div>
          <div class="ruled-line"></div>
          <div class="ruled-line"></div>
        </div>
      `;
    }

    const noteText = String(question?.interviewerNotes?.[0]?.text || "").trim();
    const noteBlock = noteText ? `<div class="interviewer-note">${escapeHtml(noteText)}</div>` : "";

    const responseHeader =
      isRankingAnswerType(question.answerType) ||
      getAnswerTypeMeta(question.answerType).supportsOptions
        ? "Options"
        : "Record the answer";

    questionBlocks.push(`
      <table class="question" role="presentation" width="100%" cellspacing="0" cellpadding="0">
        <tr>
          <td>
            <div class="question-title">${questionLabel}.&nbsp; ${escapeHtml(
              question?.prompt || "",
            )}${chips.join("")}</div>
            ${mediaCue}
            ${noteBlock}
            <div class="answer-title">${responseHeader}</div>
            ${answerBody}
            ${question.needsRoute ? '<div class="branch-note">Route not set</div>' : ""}
          </td>
        </tr>
      </table>
    `);
  });

  const decisionAppendix = decisionEntries.length
    ? `
      <div class="page-break"></div>
      <div class="appendix-title">Decision Tree</div>
      <div class="appendix-subtitle">Every answer that changes the path. Anything not listed here continues to the next question.</div>
      <table class="decision-table" role="presentation" width="100%" cellspacing="0" cellpadding="0">
        ${decisionEntries
          .map(
            (entry) => `<tr>
          <td class="decision-q">${escapeHtml(entry.questionLabel)}</td>
          <td class="decision-answer">${escapeHtml(entry.optionLabel || "")}</td>
          <td class="decision-route">\u2192 ${escapeHtml(entry.annotation || "")}</td>
        </tr>`,
          )
          .join("")}
      </table>
    `
    : "";

  const campaignCode = String(campaign?.code || "DRAFT").toUpperCase();
  const documentTitle = `SURVEY GUIDE (${campaignCode})`;
  const campaignLocation = escapeHtml(campaign?.location || "-");
  const campaignName = escapeHtml(campaign?.title || "-");
  const campaignDate = escapeHtml(campaign?.date || "-");

  return `
<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(documentTitle)}</title>
    <!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument></xml><![endif]-->
    <style>
      @page { size: 8.5in 11in; margin: 0.75in 0.8in; }
      body { margin: 0; }
      body {
        font-family: "Segoe UI", Calibri, Arial, sans-serif;
        color: #111827;
        font-size: 10pt;
        line-height: 1.45;
      }
      h1 {
        margin: 0;
        font-size: 19pt;
        font-weight: 700;
      }
      .meta-line {
        font-size: 10.5pt;
        color: #6b7280;
        margin: 4pt 0 0 0;
      }
      .meta-sub {
        font-size: 9pt;
        color: #6b7280;
        margin: 3pt 0 0 0;
      }
      .divider {
        border-bottom: 2.5pt solid #111827;
        margin: 9pt 0 4pt 0;
      }
      .note-box {
        margin: 0 0 10pt 0;
        border-collapse: collapse;
      }
      .note-box td {
        border: 1pt solid #c9ced8;
        border-left: 4pt solid #b45309;
        background: #fffbeb;
        padding: 7pt 9pt;
        font-size: 9.5pt;
        color: #7c2d12;
      }
      .section {
        font-size: 12.5pt;
        font-weight: 700;
        color: #111827;
        border-bottom: 1.5pt solid #111827;
        padding: 0 0 4pt 0;
        margin: 18pt 0 11pt 0;
        page-break-after: avoid;
      }
      .question {
        margin: 0 0 12pt 0;
        border-collapse: collapse;
        page-break-inside: avoid;
      }
      .question td {
        border: 1pt solid #c9ced8;
        padding: 10pt 11pt;
      }
      .question-title {
        font-size: 11pt;
        font-weight: 700;
        color: #111827;
        line-height: 1.4;
        margin: 0 0 8pt 0;
      }
      .chip {
        font-size: 8pt;
        font-weight: 700;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: #6b7280;
        border: 0.75pt solid #6b7280;
        border-radius: 8pt;
        padding: 1pt 5pt;
        margin-left: 6pt;
        white-space: nowrap;
      }
      .chip-anchor {
        color: #1f3a8a;
        border-color: #1f3a8a;
      }
      .media-cue {
        font-size: 9pt;
        color: #1f3a8a;
        margin: 0 0 7pt 0;
      }
      .interviewer-note {
        font-size: 9pt;
        color: #6b7280;
        font-style: italic;
        margin: 0 0 8pt 0;
      }
      .answer-title {
        font-size: 8.5pt;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #6b7280;
        margin: 0 0 5pt 0;
      }
      .option-grid {
        width: 100%;
        border-collapse: collapse;
      }
      .option-cell {
        width: 50%;
        vertical-align: top;
        padding: 3pt 8pt 3pt 0;
        font-size: 10pt;
        color: #111827;
      }
      .option-cell-empty {
        padding: 3pt 0;
      }
      .checkbox {
        display: inline-block;
        width: 10px;
        height: 10px;
        border: 0.75pt solid #111827;
        margin-right: 6pt;
      }
      .branch-note {
        color: #1f3a8a;
        font-size: 8.5pt;
      }
      .ranking-items {
        margin: 0;
        padding: 0;
        list-style: none;
      }
      .ranking-items li {
        display: inline-block;
        width: 48%;
        vertical-align: top;
        margin: 0 8pt 4pt 0;
      }
      .rank-slots {
        margin: 0;
        padding: 0;
        list-style: none;
      }
      .rank-slots li {
        margin: 0 0 6pt 0;
      }
      .slot-index {
        display: inline-block;
        width: 7%;
        font-weight: 700;
        color: #1f3a8a;
      }
      .slot-line {
        display: inline-block;
        width: 90%;
        border-bottom: 0.75pt solid #c9ced8;
        height: 16pt;
      }
      .ruled-lines {
        margin-top: 0;
      }
      .ruled-line {
        border-bottom: 0.75pt solid #c9ced8;
        height: 15pt;
      }
      .page-break {
        page-break-before: always;
      }
      .appendix-title {
        font-size: 13pt;
        font-weight: 700;
        color: #111827;
        margin: 0 0 3pt 0;
      }
      .appendix-subtitle {
        font-size: 9.5pt;
        color: #6b7280;
        margin: 0 0 12pt 0;
      }
      .decision-table {
        border-collapse: collapse;
      }
      .decision-table td {
        border-bottom: 0.75pt solid #c9ced8;
        padding: 6pt 8pt 6pt 0;
        font-size: 10pt;
      }
      .decision-q {
        width: 12%;
        font-weight: 700;
        color: #1f3a8a;
      }
      .decision-answer {
        width: 48%;
      }
      .decision-route {
        width: 40%;
      }
      .closing-box {
        margin: 16pt 0 0 0;
        border-collapse: collapse;
      }
      .closing-box td {
        border: 1pt solid #c9ced8;
        background: #f8fafc;
        padding: 11pt;
      }
      .closing-label {
        font-size: 8.5pt;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #6b7280;
        margin: 0 0 5pt 0;
      }
      .closing-text {
        font-size: 11pt;
        color: #111827;
        line-height: 1.5;
      }
      .closing-reminder {
        font-size: 9pt;
        color: #6b7280;
        font-style: italic;
        margin: 7pt 0 0 0;
      }
      .final-note {
        margin: 0 0 10pt 0;
      }
      .end-marker {
        font-size: 10pt;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-align: center;
        color: #6b7280;
        border-top: 1pt solid #c9ced8;
        padding-top: 9pt;
        margin-top: 4pt;
      }
    </style>
  </head>
  <body>
    <h1>${escapeHtml(documentTitle)}</h1>
    <div class="meta-line">${campaignLocation} \u00b7 ${escapeHtml(campaignCode)} \u2014 ${campaignName}</div>
    <div class="meta-sub">${campaignDate} \u00b7 ${flowOrderIds.length} questions</div>
    <div class="divider"></div>

    <table class="note-box" role="presentation" width="100%" cellspacing="0" cellpadding="0">
      <tr>
        <td><b>Note:</b> Read each question in full, exactly as written. Do not offer the options unless they are listed beneath the question.</td>
      </tr>
    </table>

    ${questionBlocks.join("")}

    <table class="closing-box" role="presentation" width="100%" cellspacing="0" cellpadding="0">
      <tr><td>
        <div class="closing-label">Closing</div>
        <div class="closing-text">${escapeHtml(GUIDE_CLOSING_SPOKEN_LINE)}</div>
        <div class="closing-reminder">${escapeHtml(GUIDE_WRAP_UP_REMINDER)}</div>
      </td></tr>
    </table>
    <table class="note-box final-note" role="presentation" width="100%" cellspacing="0" cellpadding="0">
      <tr>
        <td><b>Note:</b> ${escapeHtml(GUIDE_FINAL_NOTE)}</td>
      </tr>
    </table>
    <div>
      <div class="end-marker">END OF SURVEY</div>
    </div>

    ${decisionAppendix}
  </body>
</html>
  `;
};

const downloadSurveyGuideWordDocument = (html, campaignCode) => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const safeCode = sanitizeFilenamePart(campaignCode || "draft") || "draft";
  const filename = `survey-guide-${safeCode}.doc`;
  const blob = new Blob(["\ufeff", html], { type: "application/msword;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const getQuestionAnswerChip = (question) => {
  if (isRankingAnswerType(question?.answerType)) {
    const itemCount = getRankingItemCount(question);
    const rankCount = getResolvedRankCount(question, question?.options);
    return rankCount >= itemCount ? `rank all ${itemCount}` : `rank top ${rankCount}`;
  }

  const answerTypeMeta = getAnswerTypeMeta(question?.answerType);
  if (!answerTypeMeta.supportsOptions) return "short field";

  const optionCount = Array.isArray(question?.options) ? question.options.length : 0;
  return `${optionCount || 0}-choice`;
};

const getQuestionVisualTone = (question, isStart) => {
  if (isStart) {
    return {
      accent: "#10b981",
      accentSoft: "rgba(16,185,129,0.16)",
      border: "rgba(16,185,129,0.62)",
    };
  }

  return getAnswerTypeMeta(question?.answerType).supportsOptions
    ? {
        accent: "#f59e0b",
        accentSoft: "rgba(245,158,11,0.16)",
        border: "rgba(96,165,250,0.16)",
      }
    : {
        accent: "#3b82f6",
        accentSoft: "rgba(59,130,246,0.16)",
        border: "rgba(96,165,250,0.16)",
      };
};

const getLaneOffset = (lane) => {
  if (!lane) return 0;
  const distance = Math.ceil(lane / 2);
  return lane % 2 === 1 ? -distance : distance;
};

const createEmptyChoiceOptions = () => [
  { id: createOptionId(), label: "", routeTo: ROUTE_CONTINUE, cohorts: [] },
  { id: createOptionId(), label: "", routeTo: ROUTE_CONTINUE, cohorts: [] },
];

const createYesNoOptions = () => [
  { id: createOptionId(), label: "Yes", routeTo: ROUTE_CONTINUE, cohorts: [] },
  { id: createOptionId(), label: "No", routeTo: ROUTE_CONTINUE, cohorts: [] },
];

const resolveStartQuestionId = (startQuestionId, questions) => {
  if (!questions.length) return null;
  return questions.some((question) => question.id === startQuestionId)
    ? startQuestionId
    : questions[0].id;
};

const resolveRouteTarget = (routeTo, questions, index) => {
  const fallbackRoute = getSequentialRouteTarget(questions, index);
  if (!routeTo || routeTo === ROUTE_CONTINUE) return fallbackRoute;
  return routeTo;
};

const getRouteValue = (routeTo, ids, fallbackRoute) => {
  if (!routeTo || routeTo === ROUTE_CONTINUE) return ROUTE_CONTINUE;
  if (routeTo === ROUTE_END) return ROUTE_END;
  return ids.has(routeTo) ? routeTo : fallbackRoute === ROUTE_END ? ROUTE_CONTINUE : ROUTE_CONTINUE;
};

const serializeSurveyGuide = (guide, campaign) =>
  JSON.stringify({
    version: SURVEY_GUIDE_DOCUMENT_VERSION,
    campaignCode: campaign?.code || "",
    surveyType: "custom",
    savedAt: new Date().toISOString(),
    guide,
  });

const toApiQuestionsPayload = (guide) => {
  const questions = guide?.questions || [];
  const startQuestion =
    questions.find((question) => question.id === guide?.startQuestionId) || questions[0] || null;

  const idToKey = new Map(questions.map((q, index) => [q.id, q.code || `q${index + 1}`]));

  const resolveNextKey = (routeTo, fallbackRouteTo) => {
    const effectiveRoute =
      !routeTo || routeTo === ROUTE_CONTINUE ? fallbackRouteTo || ROUTE_END : routeTo;

    if (!effectiveRoute || effectiveRoute === ROUTE_END) return null;
    return idToKey.get(effectiveRoute) || null;
  };

  const TYPE_MAP = {
    multipleChoice: "multiple_choice",
    shortText: "acknowledgement",
    ranking: "ranking",
    multiple_choice: "multiple_choice",
    acknowledgement: "acknowledgement",
    rank: "ranking",
    ranked_choice: "ranking",
  };

  return {
    startKey: startQuestion ? idToKey.get(startQuestion.id) || "q1" : null,
    standardQuestions: STANDARD_PRELIMINARY_QUESTIONS.map((question, index) => ({
      key: question.key,
      label: question.label,
      prompt: question.prompt,
      position: index + 1,
      required: true,
      demographicField: question.demographicField,
      overrideTranscription: question.overrideTranscription,
    })),
    questions: questions.map((q, index) => {
      const key = idToKey.get(q.id) || `q${index + 1}`;
      const supportsOptions = getAnswerTypeMeta(q.answerType).supportsOptions;
      const hasOptionRouting = usesOptionRouting(q.answerType);
      const defaultNextRouteTo = questions[index + 1]?.id || ROUTE_END;
      const mediaKey = q.bankQuestionId ? undefined : normalizeMediaKey(q.mediaKey);
      const campaignQuestionId = normalizeEntityId(q.campaignQuestionId);

      return {
        ...(campaignQuestionId != null ? { campaignQuestionId } : {}),
        bankQuestionId: q.bankQuestionId || null,
        bankQuestionCode: q.bankQuestionCode || null,
        needsRoute: Boolean(q.needsRoute),
        key,
        type: TYPE_MAP[q.answerType] || (supportsOptions ? "multiple_choice" : "acknowledgement"),
        section: q.section || "",
        prompt: q.prompt || "",
        helperText: q.interviewerNotes?.[0]?.text || "",
        interviewerNote: q.interviewerNotes?.[0]?.text || "",
        ...(mediaKey !== undefined ? { mediaKey } : {}),
        position: index + 1,
        ...(isRankingAnswerType(q.answerType)
          ? { rankCount: getResolvedRankCount(q, q.options) }
          : {}),
        nextKey: hasOptionRouting ? null : resolveNextKey(q.routeTo, defaultNextRouteTo),
        options: supportsOptions
          ? (q.options || []).map((opt, optIndex) => {
              const campaignQuestionOptionId = normalizeEntityId(opt.campaignQuestionOptionId);
              const optionPayload = {
                ...(campaignQuestionOptionId != null ? { campaignQuestionOptionId } : {}),
                label: opt.label || "",
                position: optIndex + 1,
                cohortIds: (opt.cohorts || []).map((c) => c.cohortId),
                needsRoute: Boolean(opt.needsRoute),
              };

              if (hasOptionRouting) {
                return {
                  ...optionPayload,
                  nextKey: resolveNextKey(opt.routeTo, defaultNextRouteTo),
                };
              }

              return optionPayload;
            })
          : [],
      };
    }),
  };
};

const parseSurveyGuideDocument = (rawDocument) => {
  if (!rawDocument) return null;

  const parsed = typeof rawDocument === "string" ? JSON.parse(rawDocument) : rawDocument;

  if (parsed?.guide) return parsed.guide;
  if (Array.isArray(parsed?.questions)) return parsed;
  return null;
};

const buildGuideFromApiQuestions = (questionsData, campaign) => {
  const apiQuestions = Array.isArray(questionsData?.questions) ? questionsData.questions : [];
  if (!apiQuestions.length) return null;

  const orderedQuestions = [...apiQuestions].sort((a, b) => {
    const left = Number(a?.position || 0);
    const right = Number(b?.position || 0);
    return left - right;
  });

  const withRefs = orderedQuestions.map((question, index) => ({
    raw: question,
    local: {
      id: createQuestionId(),
      campaignQuestionId: normalizeEntityId(question?.campaignQuestionId),
      bankQuestionId: question?.bankQuestionId || null,
      bankQuestionCode: question?.bankQuestionCode || null,
      needsRoute: Boolean(question?.needsRoute),
      code: String(question?.key || `q${index + 1}`),
      section: question?.section != null ? String(question.section) : "Untitled Section",
      prompt: question?.prompt != null ? String(question.prompt) : `Question ${index + 1}`,
      answerType: normalizeAnswerType(question?.type),
      interviewerNotes: normalizeInterviewerNotes(question),
      options: [],
      routeTo: ROUTE_CONTINUE,
      mediaType: resolveMediaType(question),
      mediaKey: normalizeMediaKey(getFirstNonEmptyString(question?.mediaKey, question?.media_key)),
      mediaUrl: resolveMediaUrl(question),
      mediaName:
        getFirstNonEmptyString(question?.mediaName, question?.media_name) ||
        (getFirstNonEmptyString(question?.mediaKey, question?.media_key)
          ? getMediaFilenameFromKey(getFirstNonEmptyString(question?.mediaKey, question?.media_key))
          : ""),
      mediaUploading: false,
      mediaUploadError: null,
      expanded: index === 0,
    },
  }));
  const normalizedLocalQuestions = ensureUniqueQuestionKeys(withRefs.map((entry) => entry.local));
  const normalizedWithRefs = withRefs.map((entry, index) => ({
    ...entry,
    local: normalizedLocalQuestions[index],
  }));
  const localQuestions = normalizedWithRefs.map((entry) => entry.local);

  const localByCampaignQuestionId = new Map();
  const localByKey = new Map();

  normalizedWithRefs.forEach(({ local }) => {
    if (local.campaignQuestionId != null) {
      localByCampaignQuestionId.set(String(local.campaignQuestionId), local.id);
    }

    if (local.code) {
      localByKey.set(String(local.code).toLowerCase(), local.id);
    }
  });

  const resolveApiRouteRef = (targetRef) => {
    if (targetRef == null || targetRef === "") return ROUTE_END;

    const asString = String(targetRef).trim();
    if (!asString) return ROUTE_END;

    if (localByCampaignQuestionId.has(asString)) {
      return localByCampaignQuestionId.get(asString);
    }

    const normalizedKey = asString.toLowerCase();
    if (localByKey.has(normalizedKey)) {
      return localByKey.get(normalizedKey);
    }

    return ROUTE_CONTINUE;
  };

  const populatedQuestions = normalizedWithRefs.map(({ raw, local }, index) => {
    const defaultRoute = getSequentialRouteTarget(localQuestions, index);
    const supportsOptions = getAnswerTypeMeta(local.answerType).supportsOptions;
    const hasOptionRouting = usesOptionRouting(local.answerType);
    const routeRef = raw?.nextQuestionId ?? raw?.nextKey ?? null;
    const resolvedRoute = resolveApiRouteRef(routeRef);

    if (supportsOptions) {
      const options = (Array.isArray(raw?.options) ? raw.options : []).map((option) => {
        const nextRef = option?.nextQuestionId ?? option?.nextKey ?? null;
        const routeTo = hasOptionRouting ? resolveApiRouteRef(nextRef) : ROUTE_CONTINUE;
        const needsRoute = Boolean(option?.needsRoute || (raw?.needsRoute && hasOptionRouting));

        return {
          id: createOptionId(),
          campaignQuestionOptionId: normalizeEntityId(option?.campaignQuestionOptionId),
          label: option?.label != null ? String(option.label) : "",
          needsRoute,
          routeTo: needsRoute
            ? ""
            : hasOptionRouting
              ? routeTo === ROUTE_CONTINUE
                ? defaultRoute
                : routeTo
              : ROUTE_CONTINUE,
          cohorts: Array.isArray(option?.cohorts) ? option.cohorts : [],
        };
      });

      return {
        ...local,
        rankCount: normalizeRankCount(raw, local.answerType, options),
        needsRoute: Boolean(raw?.needsRoute && !hasOptionRouting),
        routeTo: hasOptionRouting
          ? ROUTE_CONTINUE
          : raw?.needsRoute
            ? ""
            : resolvedRoute === ROUTE_CONTINUE
              ? defaultRoute
              : resolvedRoute,
        options: options.length ? options : createEmptyChoiceOptions(),
      };
    }

    return {
      ...local,
      rankCount: null,
      routeTo: raw?.needsRoute
        ? ""
        : resolvedRoute === ROUTE_CONTINUE
          ? defaultRoute
          : resolvedRoute,
    };
  });

  let startQuestionId = null;
  const rawStartQuestionId = questionsData?.startQuestionId;
  const rawStartKey = questionsData?.startKey;

  if (rawStartQuestionId != null && rawStartQuestionId !== "") {
    startQuestionId = localByCampaignQuestionId.get(String(rawStartQuestionId)) || null;
  }

  if (!startQuestionId && rawStartKey) {
    startQuestionId = localByKey.get(String(rawStartKey).toLowerCase()) || null;
  }

  return normalizeGuide(
    {
      title: `${campaign?.title || campaign?.code || "Campaign"} Survey Guide`,
      startQuestionId,
      questions: populatedQuestions,
    },
    campaign,
  );
};

const buildRoutePath = (startX, startY, endX, endY) => {
  const midpointY = startY + Math.max(34, (endY - startY) / 2);
  if (Math.abs(endX - startX) < 18) {
    return `M ${startX} ${startY} L ${endX} ${endY}`;
  }

  return [
    `M ${startX} ${startY}`,
    `C ${startX} ${midpointY}, ${endX} ${midpointY}, ${endX} ${endY}`,
  ].join(" ");
};

const buildArrowPolygonPoints = (x, y) => `${x - 4.5},${y - 8} ${x + 4.5},${y - 8} ${x},${y - 1}`;

const getMapFitScale = (graph, viewport, maxScale) => {
  if (!graph?.width || !graph?.height || !viewport) return 1;

  const availableWidth = Math.max(0, viewport.clientWidth - 32);
  const availableHeight = Math.max(0, viewport.clientHeight - 32);
  if (!availableWidth || !availableHeight) return 1;

  const widthScale = availableWidth / graph.width;
  const heightScale = availableHeight / graph.height;
  return clamp(Math.min(widthScale, heightScale), 0.36, maxScale);
};

const getViewportCenterScroll = (graph, viewport, scale) => {
  if (!graph?.width || !graph?.height || !viewport) {
    return { left: 0, top: 0 };
  }

  const contentWidth = graph.width * scale;
  const contentHeight = graph.height * scale;

  return {
    left: Math.max(0, (contentWidth - viewport.clientWidth) / 2),
    top: Math.max(0, (contentHeight - viewport.clientHeight) / 2),
  };
};

const buildSurveyFlowGraph = (questions) => {
  if (!Array.isArray(questions) || !questions.length) {
    return {
      nodes: [],
      edges: [],
      width: MAP_DIMENSIONS.nodeWidth + MAP_DIMENSIONS.paddingX * 2,
      height: 320,
    };
  }

  const questionIndexById = new Map(questions.map((question, index) => [question.id, index]));
  const laneById = { [questions[0].id]: 0 };
  let nextLane = 1;

  const edges = [];

  questions.forEach((question, index) => {
    const currentLane = laneById[question.id] ?? 0;
    laneById[question.id] = currentLane;

    const nextSequentialId = getSequentialRouteTarget(questions, index);
    const questionEdges = usesOptionRouting(question.answerType)
      ? question.options
          .filter((option) => !option.needsRoute)
          .map((option) => {
            const targetId =
              !option.routeTo || option.routeTo === ROUTE_CONTINUE
                ? nextSequentialId
                : option.routeTo;

            return {
              id: `${question.id}:${option.id}`,
              sourceId: question.id,
              targetId,
              kind:
                targetId === ROUTE_END
                  ? "end"
                  : targetId === nextSequentialId
                    ? "sequential"
                    : "branch",
              label: option.label,
            };
          })
      : question.needsRoute
        ? []
        : (() => {
            const targetId =
              !question.routeTo || question.routeTo === ROUTE_CONTINUE
                ? nextSequentialId
                : question.routeTo;

            return [
              {
                id: `${question.id}:direct`,
                sourceId: question.id,
                targetId,
                kind:
                  targetId === ROUTE_END
                    ? "end"
                    : targetId === nextSequentialId
                      ? "sequential"
                      : "branch",
                label: "Direct response",
              },
            ];
          })();

    questionEdges.forEach((edge) => {
      if (edge.targetId !== ROUTE_END && laneById[edge.targetId] == null) {
        const targetIndex = questionIndexById.get(edge.targetId);
        const keepLane = edge.kind === "sequential" && targetIndex === index + 1;
        laneById[edge.targetId] = keepLane ? currentLane : nextLane++;
      }
    });

    edges.push(...questionEdges);
  });

  questions.forEach((question) => {
    if (laneById[question.id] == null) {
      laneById[question.id] = nextLane++;
    }
  });

  const usedLanes = Object.values(laneById);
  const laneCount = usedLanes.length ? Math.max(...usedLanes) + 1 : 1;
  const laneOffsets = Array.from({ length: laneCount }, (_, lane) => getLaneOffset(lane));
  const minOffset = Math.min(...laneOffsets);
  const maxOffset = Math.max(...laneOffsets);
  const centerX =
    MAP_DIMENSIONS.paddingX +
    Math.abs(minOffset) * MAP_DIMENSIONS.laneGap +
    MAP_DIMENSIONS.nodeWidth / 2;
  const endSourceLanes = edges
    .filter((edge) => edge.targetId === ROUTE_END)
    .map((edge) => laneById[edge.sourceId] ?? 0);
  const averageEndLane = endSourceLanes.length
    ? endSourceLanes.reduce((sum, lane) => sum + lane, 0) / endSourceLanes.length
    : 0;
  const averageEndOffset =
    endSourceLanes.length > 0
      ? endSourceLanes.reduce((sum, lane) => sum + getLaneOffset(lane), 0) / endSourceLanes.length
      : 0;

  const positionedNodes = questions.map((question, index) => ({
    id: question.id,
    kind: "question",
    row: index,
    lane: laneById[question.id] ?? 0,
    width: MAP_DIMENSIONS.nodeWidth,
    height: MAP_DIMENSIONS.nodeHeight,
    x:
      centerX +
      getLaneOffset(laneById[question.id] ?? 0) * MAP_DIMENSIONS.laneGap -
      MAP_DIMENSIONS.nodeWidth / 2,
    y: MAP_DIMENSIONS.paddingY + index * MAP_DIMENSIONS.rowGap,
    index,
    question,
  }));

  const endNode = {
    id: ROUTE_END,
    kind: "end",
    row: questions.length,
    lane: averageEndLane,
    width: MAP_DIMENSIONS.endWidth,
    height: MAP_DIMENSIONS.endHeight,
    x: centerX + averageEndOffset * MAP_DIMENSIONS.laneGap - MAP_DIMENSIONS.endWidth / 2,
    y: MAP_DIMENSIONS.paddingY + questions.length * MAP_DIMENSIONS.rowGap,
  };

  const allNodes = [...positionedNodes, endNode];

  const width = Math.max(
    MAP_DIMENSIONS.paddingX * 2 +
      (maxOffset - minOffset) * MAP_DIMENSIONS.laneGap +
      MAP_DIMENSIONS.nodeWidth,
    endNode.x + endNode.width + MAP_DIMENSIONS.paddingX,
  );
  const height = endNode.y + endNode.height + MAP_DIMENSIONS.paddingY;

  const contentMinX = Math.min(...allNodes.map((node) => node.x));
  const contentMaxX = Math.max(...allNodes.map((node) => node.x + node.width));
  const contentWidth = contentMaxX - contentMinX;
  const centeredMinX = (width - contentWidth) / 2;
  const shiftX = centeredMinX - contentMinX;

  const centeredNodes = allNodes.map((node) => ({
    ...node,
    x: node.x + shiftX,
  }));

  const nodeById = centeredNodes.reduce((accumulator, node) => {
    accumulator[node.id] = node;
    return accumulator;
  }, {});

  const incomingEdgeCountByTarget = edges.reduce((accumulator, edge) => {
    accumulator[edge.targetId] = (accumulator[edge.targetId] || 0) + 1;
    return accumulator;
  }, {});
  const incomingEdgeIndexByTarget = {};

  const positionedEdges = edges
    .map((edge) => {
      const sourceNode = nodeById[edge.sourceId];
      const targetNode = nodeById[edge.targetId];
      if (!sourceNode || !targetNode) return null;

      const startX = sourceNode.x + sourceNode.width / 2;
      const startY = sourceNode.y + sourceNode.height;
      const incomingCount = incomingEdgeCountByTarget[edge.targetId] || 1;
      const incomingIndex = incomingEdgeIndexByTarget[edge.targetId] || 0;
      incomingEdgeIndexByTarget[edge.targetId] = incomingIndex + 1;
      const spread =
        incomingCount > 1
          ? (incomingIndex - (incomingCount - 1) / 2) *
            Math.min(42, (targetNode.width - 26) / incomingCount)
          : 0;
      const endX = targetNode.x + targetNode.width / 2 + spread;
      const endY = targetNode.y;

      return {
        ...edge,
        path: buildRoutePath(startX, startY, endX, endY),
        arrowX: endX,
        arrowY: endY,
        labelX: startX + (endX - startX) / 2,
        labelY: startY + Math.max(22, (endY - startY) / 2) - 10,
      };
    })
    .filter(Boolean);

  return {
    nodes: centeredNodes,
    edges: positionedEdges,
    width,
    height,
  };
};

const SurveyFlowCanvas = ({
  graph,
  scale,
  variant,
  showBranchLabels,
  selectedQuestionId,
  startQuestionId,
  onNodeSelect,
}) => {
  const startNode =
    graph.nodes.find((node) => node.id === startQuestionId) || graph.nodes[0] || null;
  const onNodeKeyDown = (event, nodeId) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onNodeSelect(nodeId);
    }
  };

  return (
    <div className={styles.flowMapViewportInner}>
      <div
        className={styles.flowMapScaleBox}
        style={{ width: graph.width * scale, height: graph.height * scale }}
      >
        <div
          className={styles.flowMapCanvas}
          style={{
            width: graph.width,
            height: graph.height,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          <svg
            className={styles.flowMapSvg}
            viewBox={`0 0 ${graph.width} ${graph.height}`}
            preserveAspectRatio="xMidYMin meet"
          >
            {startNode && (
              <>
                <path
                  d={`M ${startNode.x + startNode.width / 2} ${MAP_DIMENSIONS.paddingY - 12} L ${startNode.x + startNode.width / 2} ${startNode.y}`}
                  className={`${styles.flowRoute} ${styles.flowRouteStart}`}
                />
                <polygon
                  points={buildArrowPolygonPoints(startNode.x + startNode.width / 2, startNode.y)}
                  fill="#10b981"
                />
                <text
                  x={startNode.x + startNode.width / 2}
                  y={MAP_DIMENSIONS.paddingY - 28}
                  textAnchor="middle"
                  className={styles.flowStartLabel}
                >
                  ENTRY
                </text>
              </>
            )}

            {graph.edges.map((edge) => (
              <g key={edge.id}>
                <path
                  d={edge.path}
                  className={`${styles.flowRoute} ${
                    edge.kind === "sequential"
                      ? styles.flowRouteSequential
                      : edge.kind === "branch"
                        ? styles.flowRouteBranch
                        : styles.flowRouteEnd
                  }`}
                />
                <polygon
                  points={buildArrowPolygonPoints(edge.arrowX, edge.arrowY)}
                  fill={
                    edge.kind === "sequential"
                      ? "#3b82f6"
                      : edge.kind === "branch"
                        ? "#f59e0b"
                        : "#10b981"
                  }
                />
                {showBranchLabels && edge.kind === "branch" && edge.label ? (
                  <g>
                    <rect
                      x={edge.labelX - 44}
                      y={edge.labelY - 12}
                      width="88"
                      height="16"
                      rx="8"
                      className={styles.flowRouteLabelBg}
                    />
                    <text
                      x={edge.labelX}
                      y={edge.labelY}
                      textAnchor="middle"
                      className={styles.flowRouteText}
                    >
                      {truncateText(edge.label, 18)}
                    </text>
                  </g>
                ) : null}
              </g>
            ))}

            {graph.nodes.map((node) => {
              if (node.kind === "end") {
                return (
                  <g key={node.id} className={styles.flowEndNodeGroup}>
                    <rect
                      x={node.x}
                      y={node.y}
                      width={node.width}
                      height={node.height}
                      rx="16"
                      className={styles.flowEndNodeRect}
                    />
                    <text
                      x={node.x + node.width / 2}
                      y={node.y + 20}
                      textAnchor="middle"
                      className={styles.flowEndNodeText}
                    >
                      ■ End of Survey
                    </text>
                  </g>
                );
              }

              const { question, index } = node;
              const isStart = startQuestionId === node.id;
              const tone = getQuestionVisualTone(question, isStart);
              const isSelected = selectedQuestionId === node.id;
              const codeLabel = isStart
                ? `${getQuestionCodeLabel(question, index)} · START`
                : getQuestionCodeLabel(question, index);
              const chipLabels = question?.isAnchor
                ? ["anchor", getQuestionAnswerChip(question)]
                : [getQuestionAnswerChip(question)];

              return (
                <g
                  key={node.id}
                  className={`${styles.flowQuestionGroup} ${
                    isSelected ? styles.flowQuestionGroupSelected : ""
                  }`}
                  role="button"
                  tabIndex={0}
                  onClick={() => onNodeSelect(node.id)}
                  onKeyDown={(event) => onNodeKeyDown(event, node.id)}
                >
                  <rect
                    x={node.x}
                    y={node.y}
                    width={node.width}
                    height={node.height}
                    rx="11"
                    className={styles.flowQuestionCardRect}
                    style={{ stroke: isSelected ? "rgba(147,197,253,0.95)" : tone.border }}
                  />
                  <rect
                    x={node.x}
                    y={node.y}
                    width="4"
                    height={node.height}
                    rx="2"
                    style={{ fill: tone.accent }}
                  />
                  <text
                    x={node.x + 15}
                    y={node.y + 19}
                    className={styles.flowQuestionCodeText}
                    style={{ fill: tone.accent }}
                  >
                    {codeLabel}
                  </text>
                  {chipLabels.map((chipLabel, chipIndex) => {
                    const chipWidth = Math.max(52, chipLabel.length * 6.4 + 22);
                    const xOffset = chipLabels
                      .slice(chipIndex + 1)
                      .reduce((sum, value) => sum + Math.max(52, value.length * 6.4 + 22) + 6, 0);
                    const chipX = node.x + node.width - 12 - chipWidth - xOffset;

                    return (
                      <g key={`${node.id}-chip-${chipLabel}`}>
                        <rect
                          x={chipX}
                          y={node.y + 8}
                          width={chipWidth}
                          height="16"
                          rx="8"
                          style={{ fill: tone.accentSoft }}
                        />
                        <text
                          x={chipX + chipWidth / 2}
                          y={node.y + 19.5}
                          textAnchor="middle"
                          className={styles.flowQuestionChipText}
                          style={{ fill: tone.accent }}
                        >
                          {chipLabel}
                        </text>
                      </g>
                    );
                  })}
                  <text x={node.x + 15} y={node.y + 35} className={styles.flowQuestionSectionText}>
                    {truncateText(question.section, 28)}
                  </text>
                  <text x={node.x + 15} y={node.y + 50} className={styles.flowQuestionPromptText}>
                    {truncateText(question.prompt, 36)}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    </div>
  );
};

const createDefaultQuestion = ({
  section,
  prompt,
  answerType,
  interviewerNotes = [],
  options,
  routeTo = ROUTE_CONTINUE,
}) => {
  const supportsOptions = getAnswerTypeMeta(answerType).supportsOptions;
  const normalizedOptions = options || (supportsOptions ? createEmptyChoiceOptions() : []);

  return {
    id: createQuestionId(),
    code: createQuestionKey(),
    section,
    prompt,
    answerType,
    interviewerNotes: interviewerNotes.map((note) => ({ id: createNoteId(), text: note })),
    options: normalizedOptions,
    rankCount: isRankingAnswerType(answerType)
      ? getResolvedRankCount({ rankCount: null }, normalizedOptions)
      : null,
    routeTo,
    expanded: true,
  };
};

const createDefaultGuide = (campaign) => ({
  title: `${campaign?.title || campaign?.code || "Campaign"} Survey Guide`,
  startQuestionId: getAnchorId(STANDARD_ANCHOR_QUESTIONS[0].key),
  questions: STANDARD_ANCHOR_QUESTIONS.map((definition) => createAnchorQuestion(definition)),
});

const cloneQuestionsForImport = (questions) => {
  const safeQuestions = (Array.isArray(questions) ? questions : []).filter(
    (question) => !isAnchorQuestion(question),
  );
  const questionIdMap = new Map();

  safeQuestions.forEach((question) => {
    questionIdMap.set(question.id, createQuestionId());
  });

  return safeQuestions.map((question) => {
    const nextQuestionId = questionIdMap.get(question.id);
    const supportsOptions = getAnswerTypeMeta(question.answerType).supportsOptions;
    const hasOptionRouting = usesOptionRouting(question.answerType);

    const mapRoute = (routeTo) => {
      if (!routeTo || routeTo === ROUTE_CONTINUE || routeTo === ROUTE_END) {
        return routeTo || ROUTE_CONTINUE;
      }

      return questionIdMap.get(routeTo) || ROUTE_CONTINUE;
    };

    return {
      ...question,
      id: nextQuestionId,
      campaignQuestionId: null,
      options: supportsOptions
        ? (question.options || []).map((option) => ({
            ...option,
            id: createOptionId(),
            campaignQuestionOptionId: null,
            routeTo: hasOptionRouting ? mapRoute(option.routeTo) : ROUTE_CONTINUE,
          }))
        : [],
      routeTo: hasOptionRouting ? ROUTE_CONTINUE : mapRoute(question.routeTo),
      interviewerNotes: (question.interviewerNotes || []).map((note) => ({
        ...note,
        id: createNoteId(),
      })),
      expanded: false,
    };
  });
};

const getSequentialRouteTarget = (questions, index) => questions[index + 1]?.id || ROUTE_END;

// Loaded guides store "continue" as the next question's id, so reset those to flow into the new card.
const insertQuestionAt = (questions, index, question) => {
  const insertIndex = Math.min(Math.max(index, 0), questions.length);
  const followingId = questions[insertIndex]?.id;
  const toContinue = (routeTo) =>
    routeTo === ROUTE_CONTINUE || (followingId && routeTo === followingId)
      ? ROUTE_CONTINUE
      : routeTo;

  const next = questions.map((entry, entryIndex) =>
    entryIndex === insertIndex - 1
      ? {
          ...entry,
          routeTo: toContinue(entry.routeTo),
          options: (entry.options || []).map((option) => ({
            ...option,
            routeTo: toContinue(option.routeTo),
          })),
        }
      : entry,
  );
  next.splice(insertIndex, 0, question);
  return next;
};

const normalizeAnswerType = (rawType) => {
  const normalized = String(rawType || "").trim();
  const lowered = normalized.toLowerCase();

  if (
    normalized === "multipleChoice" ||
    normalized === "singleChoice" ||
    lowered === "multiple_choice" ||
    lowered === "single_choice" ||
    lowered === "multiplechoice"
  ) {
    return "multipleChoice";
  }

  if (lowered === "acknowledgement" || lowered === "shorttext" || lowered === "short_text") {
    return "shortText";
  }

  if (lowered === "ranking" || lowered === "rank" || lowered === "ranked_choice") {
    return "ranking";
  }

  return ANSWER_TYPES.some((entry) => entry.value === normalized) ? normalized : "shortText";
};

const normalizeInterviewerNotes = (question) => {
  if (Array.isArray(question?.interviewerNotes)) {
    return question.interviewerNotes
      .map((note) => {
        if (typeof note === "string") {
          return { id: createNoteId(), text: note };
        }

        return {
          id: note?.id || createNoteId(),
          text: String(note?.text || ""),
        };
      })
      .slice(0, 1);
  }

  if (typeof question?.interviewerNote === "string" && question.interviewerNote.trim()) {
    return [{ id: createNoteId(), text: question.interviewerNote }];
  }

  if (typeof question?.helperText === "string" && question.helperText.trim()) {
    return [{ id: createNoteId(), text: question.helperText }];
  }

  return [];
};

const normalizeOptions = (question, answerType, fallbackRoute) => {
  if (!getAnswerTypeMeta(answerType).supportsOptions) return [];

  const rawOptions = Array.isArray(question?.options)
    ? question.options
    : typeof question?.options === "string"
      ? question.options.split("\n")
      : [];

  const normalized = rawOptions.map((option) => {
    if (typeof option === "string") {
      return { id: createOptionId(), label: option, routeTo: fallbackRoute, cohorts: [] };
    }

    return {
      id: option?.id || createOptionId(),
      campaignQuestionOptionId: normalizeEntityId(option?.campaignQuestionOptionId),
      label: option?.label != null ? String(option.label) : String(option?.value ?? ""),
      needsRoute: Boolean(option?.needsRoute),
      routeTo: option?.routeTo || option?.nextQuestionId || ROUTE_CONTINUE,
      cohorts: Array.isArray(option?.cohorts) ? option.cohorts : [],
    };
  });

  if (normalized.length) return normalized;
  return createEmptyChoiceOptions();
};

const normalizeRankCount = (question, answerType, options) => {
  if (!isRankingAnswerType(answerType)) return null;
  return getResolvedRankCount(question, options);
};

const sanitizeQuestions = (questions) => {
  const withAnchors = applyAnchorQuestions(questions, null);
  const ids = new Set(withAnchors.questions.map((question) => question.id));

  const normalizedQuestions = withAnchors.questions.map((question, index) => {
    const answerType = normalizeAnswerType(question?.answerType || question?.type);
    const fallbackRoute = getSequentialRouteTarget(withAnchors.questions, index);
    const routeTo = getRouteValue(question?.routeTo, ids, fallbackRoute);
    const hasOptionRouting = usesOptionRouting(answerType);
    const options = normalizeOptions(question, answerType, fallbackRoute).map((option) => {
      const nextRoute = hasOptionRouting
        ? getRouteValue(option.routeTo, ids, fallbackRoute)
        : ROUTE_CONTINUE;

      return {
        ...option,
        routeTo: nextRoute,
      };
    });
    const rankCount = normalizeRankCount(question, answerType, options);

    return {
      id: question?.id || createQuestionId(),
      campaignQuestionId: normalizeEntityId(question?.campaignQuestionId),
      bankQuestionId: question?.bankQuestionId || null,
      bankQuestionCode: question?.bankQuestionCode || null,
      needsRoute: Boolean(question?.needsRoute),
      code: question?.code != null ? String(question.code) : `q${index + 1}`,
      section: question?.section != null ? String(question.section) : "Untitled Section",
      prompt:
        question?.prompt != null
          ? String(question.prompt)
          : question?.title != null
            ? String(question.title)
            : `Question ${index + 1}`,
      answerType,
      interviewerNotes: normalizeInterviewerNotes(question),
      options,
      rankCount,
      routeTo: hasOptionRouting ? ROUTE_CONTINUE : routeTo,
      mediaType: resolveMediaType(question),
      mediaKey: normalizeMediaKey(getFirstNonEmptyString(question?.mediaKey, question?.media_key)),
      mediaUrl: resolveMediaUrl(question),
      mediaName:
        getFirstNonEmptyString(question?.mediaName, question?.media_name) ||
        getMediaFilenameFromKey(getFirstNonEmptyString(question?.mediaKey, question?.media_key)),
      mediaUploading: Boolean(question?.mediaUploading),
      mediaUploadError: question?.mediaUploadError ? String(question.mediaUploadError) : null,
      expanded: question?.expanded !== false || index === 0,
      isAnchor: isAnchorQuestion(question),
      anchorKey: question?.anchorKey,
      lockHint: question?.lockHint,
    };
  });

  return ensureUniqueQuestionKeys(normalizedQuestions);
};

const normalizeGuide = (guide, campaign) => {
  const fallback = createDefaultGuide(campaign);
  const rawQuestions = Array.isArray(guide?.questions) ? guide.questions : fallback.questions;
  const anchorResult = applyAnchorQuestions(rawQuestions, guide?.startQuestionId, {
    retireLegacy: true,
  });
  const questions = sanitizeQuestions(anchorResult.questions);

  return {
    title: String(guide?.title || fallback.title),
    startQuestionId: resolveStartQuestionId(anchorResult.startQuestionId, questions),
    questions,
    anchorMigrationNotices: anchorResult.notices,
  };
};

const getQuestionRouteTargets = (question, questions, index) => {
  if (usesOptionRouting(question.answerType)) {
    return question.options.map((option) => resolveRouteTarget(option.routeTo, questions, index));
  }

  return [resolveRouteTarget(question.routeTo, questions, index)];
};

const allPathsReachEnd = (questions, startQuestionId) => {
  if (!questions.length || !startQuestionId) return false;

  const questionById = new Map(questions.map((question) => [question.id, question]));
  const indexById = new Map(questions.map((question, index) => [question.id, index]));
  const memo = new Map();

  const visit = (questionId, visiting) => {
    if (questionId === ROUTE_END) return true;
    if (!questionById.has(questionId)) return false;
    if (memo.has(questionId)) return memo.get(questionId);
    if (visiting.has(questionId)) return false;

    visiting.add(questionId);
    const question = questionById.get(questionId);
    const index = indexById.get(questionId);
    const targets = getQuestionRouteTargets(question, questions, index);
    const result = targets.length > 0 && targets.every((targetId) => visit(targetId, visiting));
    visiting.delete(questionId);
    memo.set(questionId, result);
    return result;
  };

  return visit(startQuestionId, new Set());
};

const getStorageKey = (campaignCode) => `${SURVEY_GUIDE_STORAGE_PREFIX}${campaignCode}`;

const isLegacySeededQuestions = (questions) => {
  if (!Array.isArray(questions) || questions.length !== 3) return false;

  const prompts = questions.map((question) =>
    String(question?.prompt || question?.title || "")
      .toLowerCase()
      .trim(),
  );

  const hasConsentPrompt = prompts.some((prompt) => prompt.includes("data privacy act"));
  const hasAgePrompt = prompts.some((prompt) => prompt.includes("ilang taon"));
  const hasCandidatePrompt = prompts.some((prompt) => prompt.includes("malakas na kandidato"));

  return hasConsentPrompt && hasAgePrompt && hasCandidatePrompt;
};

const normalizeGuideWithoutLegacySeed = (guide, campaign) => {
  const normalized = normalizeGuide(guide, campaign);

  if (isLegacySeededQuestions(normalized.questions)) {
    return normalizeGuide(createDefaultGuide(campaign), campaign);
  }

  return normalized;
};

const readStoredGuide = async (campaign) => {
  if (!campaign?.code) {
    return createDefaultGuide(campaign);
  }

  if (campaign?.surveyQuestionDocument) {
    try {
      const response = await fetch(campaign.surveyQuestionDocument);
      if (response.ok) {
        const remoteGuide = parseSurveyGuideDocument(await response.text());
        if (remoteGuide) {
          return normalizeGuideWithoutLegacySeed(remoteGuide, campaign);
        }
      }
    } catch {
      // Fall back to local cache below when remote fetch is unavailable.
    }
  }

  if (typeof window === "undefined") {
    return normalizeGuide(createDefaultGuide(campaign), campaign);
  }

  try {
    const stored = window.localStorage.getItem(getStorageKey(campaign.code));
    if (!stored) return normalizeGuide(createDefaultGuide(campaign), campaign);
    return normalizeGuideWithoutLegacySeed(
      parseSurveyGuideDocument(stored) || JSON.parse(stored),
      campaign,
    );
  } catch {
    return normalizeGuide(createDefaultGuide(campaign), campaign);
  }
};

// ─── Cohort picker ───────────────────────────────────────────────────────────

const CohortPickerTrigger = React.memo(
  ({ isOpen, onToggle, groupedCohorts, search, onSearch, onSelect, onClose }) => {
    const wrapRef = useRef(null);

    useEffect(() => {
      if (!isOpen) return undefined;
      const handler = (e) => {
        if (!wrapRef.current?.contains(e.target)) onClose();
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, [isOpen, onClose]);

    const filteredGroups = useMemo(() => {
      const q = search.trim().toLowerCase();
      if (!q) return groupedCohorts;
      return groupedCohorts
        .map(([g, cohorts]) => [g, cohorts.filter((c) => c.name.toLowerCase().includes(q))])
        .filter(([, cohorts]) => cohorts.length > 0);
    }, [groupedCohorts, search]);

    return (
      <div ref={wrapRef} className={styles.cohortPickerWrap}>
        <button type="button" className={styles.addCohortBtn} onClick={onToggle}>
          + Cohort
        </button>
        {isOpen && (
          <div className={styles.cohortPickerDropdown}>
            <div className={styles.cohortPickerSearchRow}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                className={styles.cohortPickerSearchInput}
                placeholder="Search cohorts..."
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className={styles.cohortPickerList}>
              {filteredGroups.length === 0 && (
                <p className={styles.cohortPickerEmpty}>No cohorts match.</p>
              )}
              {filteredGroups.map(([groupName, cohorts]) => (
                <div key={groupName}>
                  <p className={styles.cohortPickerGroupLabel}>{String(groupName).toUpperCase()}</p>
                  {cohorts.map((cohort) => (
                    <button
                      key={cohort.cohortId}
                      type="button"
                      className={styles.cohortPickerItem}
                      onClick={() => onSelect(cohort)}
                    >
                      <span
                        className={styles.cohortPickerDot}
                        style={{ background: cohort.color }}
                      />
                      {cohort.name}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            <div className={styles.cohortPickerFooter}>
              <Link to={Path.Cohorts} className={styles.cohortPickerManageLink} onClick={onClose}>
                Manage cohorts →
              </Link>
            </div>
          </div>
        )}
      </div>
    );
  },
);

const SurveyBuilderPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { campaignCode } = useParams();
  const questionRefs = useRef({});
  const pendingQuestionScrollIdRef = useRef(null);
  const inlineMapViewportRef = useRef(null);
  const expandedMapViewportRef = useRef(null);

  const routedCampaign = useMemo(
    () => normalizeCampaign(location.state?.campaign),
    [location.state],
  );
  const routedQuestions = useMemo(
    () => (Array.isArray(location.state?.questions) ? location.state.questions : null),
    [location.state],
  );
  const [campaign, setCampaign] = useState(() =>
    routedCampaign?.code?.toLowerCase() === String(campaignCode || "").toLowerCase()
      ? routedCampaign
      : null,
  );
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(!campaign);
  const [error, setError] = useState(null);
  const [saveState, setSaveState] = useState("idle");
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [inlineMapScale, setInlineMapScale] = useState(1);
  const [expandedMapScale, setExpandedMapScale] = useState(1);
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [dragOverQuestionId, setDragOverQuestionId] = useState(null);
  const [questionBankModalOpen, setQuestionBankModalOpen] = useState(false);
  const [openInserterIndex, setOpenInserterIndex] = useState(null);
  // null inserts bank picks at the end; advances after each pick to keep pick order.
  const bankInsertIndexRef = useRef(null);
  const [questionBankQuestions, setQuestionBankQuestions] = useState([]);
  const [questionBankLoading, setQuestionBankLoading] = useState(false);
  const [questionBankError, setQuestionBankError] = useState(null);
  const [questionBankSearch, setQuestionBankSearch] = useState("");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importMode, setImportMode] = useState("replace");
  const [importCampaigns, setImportCampaigns] = useState([]);
  const [loadingImportCampaigns, setLoadingImportCampaigns] = useState(false);
  const [selectedImportCampaignId, setSelectedImportCampaignId] = useState(null);
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [allCohorts, setAllCohorts] = useState([]);
  const [openCohortPickerOptionId, setOpenCohortPickerOptionId] = useState(null);
  const [cohortPickerSearch, setCohortPickerSearch] = useState("");
  const [anchorMigrationNotices, setAnchorMigrationNotices] = useState([]);

  useEffect(() => {
    getCohorts()
      .then((data) => setAllCohorts(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let isMounted = true;
    setQuestionBankLoading(true);
    getQuestionBankRequest()
      .then((rows) => {
        if (isMounted) setQuestionBankQuestions(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (isMounted) setQuestionBankError("Unable to load Question Bank questions.");
      })
      .finally(() => {
        if (isMounted) setQuestionBankLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const groupedCohorts = useMemo(() => {
    const map = new Map();
    for (const cohort of allCohorts) {
      const g = cohort.cohortGroupName || "Uncategorized";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(cohort);
    }
    return [...map.entries()].sort(([a], [b]) => {
      if (a === "Uncategorized") return 1;
      if (b === "Uncategorized") return -1;
      return a.localeCompare(b);
    });
  }, [allCohorts]);

  useEffect(() => {
    let isMounted = true;

    if (routedCampaign?.code?.toLowerCase() === String(campaignCode || "").toLowerCase()) {
      setCampaign(routedCampaign);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    getCampaignsRequest()
      .then((data) => {
        if (!isMounted) return;

        const rows = Array.isArray(data?.campaigns)
          ? data.campaigns
          : Array.isArray(data)
            ? data
            : [];

        const match = rows
          .map((item) => normalizeCampaign(item))
          .find(
            (item) =>
              String(item?.code || "").toLowerCase() === String(campaignCode || "").toLowerCase(),
          );

        if (!match) {
          setCampaign(null);
          setError("Campaign not found.");
          return;
        }

        setCampaign(mergeCampaignFromRoute(match, routedCampaign));
      })
      .catch(() => {
        if (!isMounted) return;
        setCampaign(null);
        setError("Failed to load survey guide.");
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [campaignCode, routedCampaign]);

  useEffect(() => {
    let isMounted = true;

    if (!campaign?.code) return undefined;

    const loadGuide = async () => {
      try {
        if (campaign?.campaignId) {
          const apiQuestionsData = await getCampaignQuestionsRequest({
            campaignId: campaign.campaignId,
          });

          const apiGuide = buildGuideFromApiQuestions(apiQuestionsData, campaign);

          if (apiGuide) {
            if (!isMounted) return;
            setGuide(apiGuide);
            setAnchorMigrationNotices(apiGuide.anchorMigrationNotices || []);
            setSaveState("saved");
            setSaveFeedback(null);
            return;
          }
        }

        const nextGuide = await readStoredGuide(campaign);
        if (!isMounted) return;

        const hasStoredQuestions =
          Array.isArray(nextGuide?.questions) && nextGuide.questions.length > 0;

        const routedGuide = buildGuideFromApiQuestions({ questions: routedQuestions }, campaign);
        const hasUsableRoutedQuestions = Boolean(routedGuide);

        if (!hasStoredQuestions && hasUsableRoutedQuestions) {
          setGuide(routedGuide);
          setAnchorMigrationNotices(routedGuide.anchorMigrationNotices || []);
          setSaveState("idle");
          setSaveFeedback(null);
          return;
        }

        if (
          !hasStoredQuestions &&
          Array.isArray(routedQuestions) &&
          routedQuestions.length > 0 &&
          !isLegacySeededQuestions(routedQuestions)
        ) {
          const normalizedRoutedGuide = normalizeGuide({ questions: routedQuestions }, campaign);
          setGuide(normalizedRoutedGuide);
          setAnchorMigrationNotices(normalizedRoutedGuide.anchorMigrationNotices || []);
          setSaveState("idle");
          setSaveFeedback(null);
          return;
        }

        const normalizedStoredGuide = normalizeGuide(nextGuide, campaign);
        setGuide(normalizedStoredGuide);
        setAnchorMigrationNotices(normalizedStoredGuide.anchorMigrationNotices || []);
        setSaveState("saved");
        setSaveFeedback(null);
      } catch {
        if (!isMounted) return;
        setGuide(normalizeGuide(createDefaultGuide(campaign), campaign));
        setAnchorMigrationNotices([]);
        setSaveState("idle");
        setSaveFeedback("Unable to load a saved guide. Started from the local draft.");
      }
    };

    loadGuide();

    return () => {
      isMounted = false;
    };
  }, [campaign, routedQuestions]);

  useEffect(() => {
    const questionIds = new Set((guide?.questions || []).map((question) => question.id));
    setActiveQuestionId((prev) => {
      if (prev && questionIds.has(prev)) return prev;
      return guide?.questions?.[0]?.id || null;
    });
  }, [guide]);

  const goBackToCampaign = () => {
    if (saveState === "idle" || saveState === "error") {
      const shouldLeave = window.confirm(
        "You have unsaved survey guide changes. Leave the builder without saving?",
      );

      if (!shouldLeave) return;
    }

    const code = String(campaign?.code || campaignCode || "").trim();
    if (!code) {
      history.push(Path.Campaigns);
      return;
    }

    history.push(`/${encodeURIComponent(code)}`);
  };

  const navigateToQuestionBank = (target) => {
    if (
      (saveState === "idle" || saveState === "error") &&
      !window.confirm("You have unsaved survey guide changes. Leave the builder without saving?")
    ) {
      return;
    }
    history.push(target);
  };

  const updateQuestions = (updater) => {
    setGuide((prev) => {
      const questions = sanitizeQuestions(updater(prev.questions));

      return {
        ...prev,
        questions,
        startQuestionId: resolveStartQuestionId(prev.startQuestionId, questions),
      };
    });
    setSaveState("idle");
    setSaveFeedback(null);
  };

  const addQuestionFromBank = (bankQuestion) => {
    const bankQuestionId = bankQuestion?.bankQuestionId;
    if (!bankQuestionId) return;
    if (
      guide?.questions?.some(
        (question) => String(question.bankQuestionId) === String(bankQuestionId),
      )
    ) {
      return;
    }

    const id = createQuestionId();
    const answerType = normalizeAnswerType(bankQuestion.type);
    const options = getAnswerTypeMeta(answerType).supportsOptions
      ? (bankQuestion.options || []).map((option) => ({
          id: createOptionId(),
          label: typeof option === "string" ? option : String(option?.label || ""),
          routeTo: ROUTE_CONTINUE,
          cohorts: [],
        }))
      : [];
    const insertIndex = bankInsertIndexRef.current;
    const sectionSource =
      insertIndex == null
        ? guide?.questions?.[guide.questions.length - 1]
        : guide?.questions?.[insertIndex - 1];
    const question = {
      id,
      code: createQuestionKey(id),
      bankQuestionId,
      bankQuestionCode: bankQuestion.code,
      campaignQuestionId: null,
      section: sectionSource?.section || "New Section",
      prompt: bankQuestion.prompt || "",
      answerType,
      interviewerNotes: [],
      options,
      rankCount: isRankingAnswerType(answerType)
        ? getResolvedRankCount({ rankCount: null }, options)
        : null,
      routeTo: ROUTE_CONTINUE,
      expanded: true,
    };

    pendingQuestionScrollIdRef.current = id;
    updateQuestions((questions) =>
      insertIndex == null
        ? [...questions, question]
        : insertQuestionAt(questions, insertIndex, question),
    );
    if (insertIndex != null) bankInsertIndexRef.current = insertIndex + 1;
    setActiveQuestionId(id);
    setSaveFeedback(`${bankQuestion.code} added to this survey guide.`);
  };

  const setStartQuestion = (questionId) => {
    setGuide((prev) => ({ ...prev, startQuestionId: questionId }));
    setSaveState("idle");
    setSaveFeedback(null);
  };

  const onQuestionChange = (questionId, field, value) => {
    updateQuestions((questions) =>
      questions.map((question) =>
        question.id === questionId
          ? question.isAnchor && ["section", "prompt"].includes(field)
            ? question
            : {
                ...question,
                [field]: value,
                ...(field === "routeTo" ? { needsRoute: false } : {}),
              }
          : question,
      ),
    );
  };

  const onQuestionTypeChange = (questionId, answerType) => {
    updateQuestions((questions) =>
      questions.map((question) => {
        if (question.id !== questionId) return question;
        if (question.isAnchor || isCannedQuestion(question)) return question;

        const supportsOptions = getAnswerTypeMeta(answerType).supportsOptions;
        const hasOptionRouting = usesOptionRouting(answerType);
        const nextOptions = supportsOptions
          ? question.options.length
            ? question.options
            : createEmptyChoiceOptions()
          : [];

        return {
          ...question,
          answerType,
          options: nextOptions,
          rankCount: isRankingAnswerType(answerType)
            ? getResolvedRankCount(question, nextOptions)
            : null,
          routeTo: hasOptionRouting ? ROUTE_CONTINUE : question.routeTo,
        };
      }),
    );
  };

  const applyYesNoTemplate = (questionId) => {
    updateQuestions((questions) =>
      questions.map((question) =>
        question.id === questionId && !isCannedQuestion(question)
          ? {
              ...question,
              answerType: "multipleChoice",
              options: createYesNoOptions(),
            }
          : question,
      ),
    );
  };

  const addQuestion = (insertIndex = null) => {
    const sectionSource =
      insertIndex == null
        ? guide?.questions?.[guide.questions.length - 1]
        : guide?.questions?.[insertIndex - 1];
    const question = createDefaultQuestion({
      section: sectionSource?.section || "New Section",
      prompt: "",
      answerType: "shortText",
      interviewerNotes: [],
    });

    pendingQuestionScrollIdRef.current = question.id;
    setOpenInserterIndex(null);

    setGuide((prev) => {
      const previousQuestions = prev?.questions || [];
      const questions = sanitizeQuestions(
        insertIndex == null
          ? [...previousQuestions, question]
          : insertQuestionAt(previousQuestions, insertIndex, question),
      );
      return {
        ...(prev || { title: "Survey Guide" }),
        questions,
        startQuestionId:
          prev?.startQuestionId || questions.length === 1
            ? resolveStartQuestionId(prev?.startQuestionId || question.id, questions)
            : resolveStartQuestionId(prev?.startQuestionId, questions),
      };
    });

    setSaveState("idle");
    setSaveFeedback(null);
    setActiveQuestionId(question.id);
  };

  const removeQuestion = (questionId) => {
    const targetQuestion = guide?.questions?.find((question) => question.id === questionId);
    if (targetQuestion?.isAnchor) {
      setSaveFeedback(ANCHOR_DELETE_BLOCK_REASON);
      return;
    }

    const questionLabel = guide?.questions?.find((question) => question.id === questionId)?.code;
    const shouldDelete = window.confirm(
      `Delete ${String(questionLabel || "this question").toUpperCase()} and reset any routes pointing to it to Continue?`,
    );

    if (!shouldDelete) return;

    updateQuestions((questions) => questions.filter((question) => question.id !== questionId));
  };

  const moveQuestion = (questionId, direction) => {
    updateQuestions((questions) => {
      const index = questions.findIndex((question) => question.id === questionId);
      const targetIndex = index + direction;
      if (index < 0 || targetIndex < 0 || targetIndex >= questions.length) return questions;

      const next = [...questions];
      const [question] = next.splice(index, 1);
      next.splice(targetIndex, 0, question);
      return next;
    });
  };

  const addQuestionOption = (questionId) => {
    updateQuestions((questions) =>
      questions.map((question) =>
        question.id === questionId && !question.isAnchor && !isCannedQuestion(question)
          ? {
              ...question,
              options: [
                ...question.options,
                {
                  id: createOptionId(),
                  label: "",
                  routeTo: ROUTE_CONTINUE,
                  cohorts: [],
                },
              ],
            }
          : question,
      ),
    );
  };

  const updateQuestionOption = (questionId, optionId, field, value) => {
    updateQuestions((questions) =>
      questions.map((question) => {
        if (question.id !== questionId) return question;
        if (question.isAnchor && field === "label") return question;
        if (isCannedQuestion(question) && field === "label") return question;
        const options = question.options.map((option) =>
          option.id === optionId
            ? { ...option, [field]: value, ...(field === "routeTo" ? { needsRoute: false } : {}) }
            : option,
        );
        return {
          ...question,
          options,
          ...(field === "routeTo" && usesOptionRouting(question.answerType)
            ? { needsRoute: options.some((option) => option.needsRoute) }
            : {}),
        };
      }),
    );
  };

  const removeQuestionOption = (questionId, optionId) => {
    updateQuestions((questions) =>
      questions.map((question) => {
        if (question.id !== questionId) return question;
        if (question.isAnchor || isCannedQuestion(question)) return question;
        return {
          ...question,
          options: question.options.filter((option) => option.id !== optionId),
        };
      }),
    );
  };

  const addCohortToOption = (questionId, optionId, cohort) => {
    updateQuestions((questions) =>
      questions.map((question) => {
        if (question.id !== questionId) return question;
        return {
          ...question,
          options: question.options.map((option) => {
            if (option.id !== optionId) return option;
            const already = (option.cohorts || []).some((c) => c.cohortId === cohort.cohortId);
            if (already) return option;
            return { ...option, cohorts: [...(option.cohorts || []), cohort] };
          }),
        };
      }),
    );
  };

  const removeCohortFromOption = (questionId, optionId, cohortId) => {
    updateQuestions((questions) =>
      questions.map((question) => {
        if (question.id !== questionId) return question;
        return {
          ...question,
          options: question.options.map((option) => {
            if (option.id !== optionId) return option;
            return {
              ...option,
              cohorts: (option.cohorts || []).filter((c) => c.cohortId !== cohortId),
            };
          }),
        };
      }),
    );
  };

  const addInterviewerNote = (questionId) => {
    updateQuestions((questions) =>
      questions.map((question) =>
        question.id === questionId && !question.isAnchor
          ? {
              ...question,
              interviewerNotes: [{ id: createNoteId(), text: "" }],
            }
          : question,
      ),
    );
  };

  const updateInterviewerNote = (questionId, noteId, value) => {
    updateQuestions((questions) =>
      questions.map((question) => {
        if (question.id !== questionId) return question;
        if (question.isAnchor) return question;
        const currentNote = question.interviewerNotes[0];
        if (!currentNote || currentNote.id !== noteId) return question;
        return {
          ...question,
          interviewerNotes: [{ ...currentNote, text: value }],
        };
      }),
    );
  };

  const removeInterviewerNote = (questionId, noteId) => {
    updateQuestions((questions) =>
      questions.map((question) => {
        if (question.id !== questionId) return question;
        if (question.isAnchor) return question;
        const currentNote = question.interviewerNotes[0];
        if (!currentNote || currentNote.id !== noteId) return question;
        return {
          ...question,
          interviewerNotes: [],
        };
      }),
    );
  };

  const patchQuestionMediaState = (questionId, patch, markDirty = false) => {
    setGuide((prev) => {
      if (!prev) return prev;

      const nextQuestions = (prev.questions || []).map((question) =>
        question.id === questionId ? { ...question, ...patch } : question,
      );

      return {
        ...prev,
        questions: sanitizeQuestions(nextQuestions),
      };
    });

    if (markDirty) {
      setSaveState("idle");
    }
  };

  const detachQuestionMedia = (questionId) => {
    const targetQuestion = guide?.questions?.find((question) => question.id === questionId);
    if (targetQuestion?.isAnchor || isCannedQuestion(targetQuestion)) return;

    patchQuestionMediaState(
      questionId,
      {
        mediaKey: null,
        mediaType: null,
        mediaUrl: null,
        mediaName: "",
        mediaUploadError: null,
        mediaUploading: false,
      },
      true,
    );
    setSaveFeedback(null);
  };

  const uploadQuestionMedia = async ({ questionId, file, inputElement }) => {
    const targetQuestion = guide?.questions?.find((question) => question.id === questionId);
    if (targetQuestion?.isAnchor || isCannedQuestion(targetQuestion)) {
      if (inputElement) {
        inputElement.value = "";
      }
      return;
    }

    if (!campaign?.campaignId || !file) {
      if (inputElement) {
        inputElement.value = "";
      }
      return;
    }

    const { contentType, extension } = resolveUploadContentType(file);

    if (!contentType) {
      patchQuestionMediaState(questionId, {
        mediaUploading: false,
        mediaUploadError: "Unsupported file type. Use jpg, jpeg, png, gif, mp4, mov, or avi.",
      });
      if (inputElement) {
        inputElement.value = "";
      }
      return;
    }

    patchQuestionMediaState(questionId, {
      mediaUploading: true,
      mediaUploadError: null,
    });

    try {
      const uploadData = await getQuestionMediaUploadUrlRequest({
        campaignId: campaign.campaignId,
        contentType,
        extension: extension || undefined,
      });

      if (!uploadData?.uploadUrl || !uploadData?.key) {
        throw new Error("Upload URL generation failed.");
      }

      await uploadQuestionMediaToS3Request({
        uploadUrl: uploadData.uploadUrl,
        file,
        contentType,
      });

      patchQuestionMediaState(
        questionId,
        {
          mediaUploading: false,
          mediaUploadError: null,
          mediaKey: String(uploadData.key),
          mediaType: contentType.startsWith("video/") ? "video" : "image",
          mediaUrl: null,
          mediaName: file.name,
        },
        true,
      );
      setSaveFeedback("Media uploaded. Save survey guide to persist this attachment.");
    } catch (error) {
      patchQuestionMediaState(questionId, {
        mediaUploading: false,
        mediaUploadError: getMediaErrorMessage(error, "Failed to upload media file."),
      });
    } finally {
      if (inputElement) {
        inputElement.value = "";
      }
    }
  };

  const onMediaPanelDragOver = (event, questionId, isUploading) => {
    if (isUploading) return;
    event.preventDefault();
    setDragOverQuestionId(questionId);
  };

  const onMediaPanelDragLeave = (event, questionId) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setDragOverQuestionId((prev) => (prev === questionId ? null : prev));
  };

  const onMediaPanelDrop = (event, questionId, isUploading) => {
    event.preventDefault();
    setDragOverQuestionId((prev) => (prev === questionId ? null : prev));
    if (isUploading) return;

    const file = getFirstSupportedMediaFile(event.dataTransfer?.files);
    if (!file) {
      patchQuestionMediaState(questionId, {
        mediaUploading: false,
        mediaUploadError: "Unsupported file type. Use jpg, jpeg, png, gif, mp4, mov, or avi.",
      });
      return;
    }

    uploadQuestionMedia({ questionId, file });
  };

  const onSaveGuide = () => {
    if (!campaign?.code || !guide || typeof window === "undefined") return;
    const firstUnroutedQuestion = (guide.questions || []).find(
      (question) => question.needsRoute || question.options.some((option) => option.needsRoute),
    );
    if (firstUnroutedQuestion) {
      setSaveState("idle");
      setSaveFeedback("Set a route for each highlighted option before saving the survey guide.");
      setActiveQuestionId(firstUnroutedQuestion.id);
      window.requestAnimationFrame(() => {
        questionRefs.current[firstUnroutedQuestion.id]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
      return;
    }
    if ((guide?.questions || []).some((question) => question.mediaUploading)) {
      setSaveState("idle");
      setSaveFeedback("Please wait for media uploads to finish before saving.");
      return;
    }
    if (!guide.questions.length) {
      setSaveState("error");
      setSaveFeedback("Add at least one question before saving this survey guide.");
      return;
    }

    const payload = normalizeGuide(guide, campaign);
    const serializedGuide = serializeSurveyGuide(payload, campaign);

    try {
      window.localStorage.setItem(getStorageKey(campaign.code), serializedGuide);
    } catch {
      setSaveState("error");
      setSaveFeedback("Failed to save the local draft.");
      return;
    }

    setSaveState("saving");
    setSaveFeedback(null);

    const apiQuestionsPayload = toApiQuestionsPayload(payload);

    const saveToApi = campaign.campaignId
      ? saveCampaignQuestionsRequest({
          campaignId: campaign.campaignId,
          payload: apiQuestionsPayload,
        })
      : Promise.resolve(null);

    saveToApi
      .then((responseData) => {
        const savedGuide = buildGuideFromApiQuestions(responseData, campaign);
        setGuide(savedGuide || payload);
        setSaveState("saved");
        setSaveFeedback("Survey guide saved to this campaign.");
      })
      .catch(async (error) => {
        const errorCode =
          error?.code || error?.response?.data?.code || error?.data?.code || error?.statusCode;
        if (errorCode === "CP1038" && campaign.campaignId) {
          try {
            const latestData = await getCampaignQuestionsRequest({
              campaignId: campaign.campaignId,
            });
            const latestGuide = buildGuideFromApiQuestions(latestData, campaign);
            if (latestGuide) {
              setGuide(latestGuide);
              window.localStorage.setItem(
                getStorageKey(campaign.code),
                serializeSurveyGuide(latestGuide, campaign),
              );
              setSaveState("idle");
              setSaveFeedback(QUESTION_BANK_SAVE_ERRORS.CP1038);
              return;
            }
          } catch {
            setSaveFeedback(
              "Question Bank content changed. Reload the builder to get the latest guide.",
            );
          }
        }
        setGuide(payload);
        setSaveState("error");
        setSaveFeedback(
          getMediaErrorMessage(
            error,
            "Saved locally, but syncing survey questions to backend failed.",
          ),
        );
      });
  };

  const onExportGuide = () => {
    if (!campaign || !guide) return;

    const currentGuideState = normalizeGuide(guide, campaign);
    const documentHtml = buildSurveyGuideDocumentHtml(currentGuideState, campaign);
    downloadSurveyGuideWordDocument(documentHtml, campaign.code);
    setSaveFeedback("Survey guide exported as editable Word document.");
  };

  const onResetGuide = () => {
    setGuide(normalizeGuide(createDefaultGuide(campaign), campaign));
    setSaveState("idle");
  };

  const closeImportModal = (force = false) => {
    if (importSubmitting && !force) return;
    setImportModalOpen(false);
    setImportError(null);
    setSelectedImportCampaignId(null);
    setImportMode("replace");
  };

  const openImportModal = () => {
    setImportModalOpen(true);
    setImportError(null);
    setSelectedImportCampaignId(null);
    setImportMode("replace");
  };

  const openQuestionBankModal = (insertIndex = null) => {
    bankInsertIndexRef.current = insertIndex;
    setOpenInserterIndex(null);
    setQuestionBankSearch("");
    setQuestionBankError(null);
    setQuestionBankModalOpen(true);
  };

  useEffect(() => {
    let isMounted = true;

    if (!importModalOpen || !campaign?.campaignId) return undefined;

    setLoadingImportCampaigns(true);
    setImportError(null);

    const currentCampaignId = Number(campaign.campaignId);

    getCampaignsRequest()
      .then(async (data) => {
        const rows = Array.isArray(data?.campaigns)
          ? data.campaigns
          : Array.isArray(data)
            ? data
            : [];

        const candidates = rows
          .map((item) => normalizeCampaign(item))
          .filter((item) => item?.campaignId && Number(item.campaignId) !== currentCampaignId);

        const settled = await Promise.allSettled(
          candidates.map(async (entry) => {
            const questionsData = await getCampaignQuestionsRequest({
              campaignId: entry.campaignId,
            });

            const guideFromCampaign = buildGuideFromApiQuestions(questionsData, entry);
            const questions = guideFromCampaign?.questions || [];
            const importableQuestions = questions.filter((question) => !question.isAnchor);

            if (!importableQuestions.length) return null;

            return {
              campaignId: entry.campaignId,
              code: entry.code,
              title: entry.title,
              location: entry.location,
              date: entry.date,
              guide: guideFromCampaign,
              questionCount: importableQuestions.length,
            };
          }),
        );

        if (!isMounted) return;

        const importable = settled
          .filter((result) => result.status === "fulfilled" && result.value)
          .map((result) => result.value)
          .sort((left, right) => String(right.date || "").localeCompare(String(left.date || "")));

        setImportCampaigns(importable);

        if (!importable.length) {
          setImportError("No previous campaigns with reusable question sets were found.");
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setImportCampaigns([]);
        setImportError("Unable to load previous campaigns for importing questions.");
      })
      .finally(() => {
        if (!isMounted) return;
        setLoadingImportCampaigns(false);
      });

    return () => {
      isMounted = false;
    };
  }, [importModalOpen, campaign]);

  const onImportQuestions = async () => {
    if (!selectedImportCampaignId) {
      setImportError("Select a campaign to import questions from.");
      return;
    }

    const selected = importCampaigns.find(
      (entry) => String(entry.campaignId) === String(selectedImportCampaignId),
    );

    const selectedImportableQuestions = (selected?.guide?.questions || []).filter(
      (question) => !question.isAnchor,
    );

    if (!selectedImportableQuestions.length) {
      setImportError("Selected campaign has no question set to import.");
      return;
    }

    setImportSubmitting(true);
    setImportError(null);

    let latestQuestionBank = questionBankQuestions;
    if (selectedImportableQuestions.some(isCannedQuestion)) {
      try {
        setQuestionBankLoading(true);
        const rows = await getQuestionBankRequest();
        latestQuestionBank = Array.isArray(rows) ? rows : [];
        setQuestionBankQuestions(latestQuestionBank);
      } catch {
        setImportError("Unable to refresh Question Bank items. Try again before importing.");
        setImportSubmitting(false);
        setQuestionBankLoading(false);
        return;
      }
      setQuestionBankLoading(false);
    }

    const missingBankQuestion = selectedImportableQuestions.find(
      (question) =>
        isCannedQuestion(question) &&
        !latestQuestionBank.some(
          (entry) => String(entry.bankQuestionId) === String(question.bankQuestionId),
        ),
    );
    if (missingBankQuestion) {
      setImportError(
        `${missingBankQuestion.bankQuestionCode || "A canned question"} is no longer in the Question Bank.`,
      );
      setImportSubmitting(false);
      return;
    }

    const refreshedQuestions = selectedImportableQuestions.map((question) => {
      if (!isCannedQuestion(question)) return question;
      const bankQuestion = latestQuestionBank.find(
        (entry) => String(entry.bankQuestionId) === String(question.bankQuestionId),
      );
      if (!bankQuestion) return question;

      const answerType = normalizeAnswerType(bankQuestion.type);
      const routingTypeChanged =
        usesOptionRouting(answerType) !== usesOptionRouting(question.answerType);
      const oldOptionsByLabel = new Map(
        (question.options || []).map((option) => [option.label.trim().toLowerCase(), option]),
      );
      const options = getAnswerTypeMeta(answerType).supportsOptions
        ? (bankQuestion.options || []).map((bankOption) => {
            const label =
              typeof bankOption === "string" ? bankOption : String(bankOption?.label || "");
            const previous = oldOptionsByLabel.get(label.trim().toLowerCase());
            return {
              ...(previous || {}),
              id: createOptionId(),
              campaignQuestionOptionId: null,
              label,
              routeTo: routingTypeChanged || !previous ? "" : previous.routeTo,
              cohorts: previous?.cohorts || [],
              needsRoute:
                usesOptionRouting(answerType) &&
                (routingTypeChanged || !previous || Boolean(previous?.needsRoute)),
            };
          })
        : [];

      return {
        ...question,
        bankQuestionCode: bankQuestion.code,
        prompt: bankQuestion.prompt,
        answerType,
        options,
        needsRoute: Boolean(
          question.needsRoute || (!usesOptionRouting(answerType) && routingTypeChanged),
        ),
        mediaType: null,
        mediaKey: null,
        mediaUrl: null,
        mediaName: "",
      };
    });
    const importedQuestions = cloneQuestionsForImport(refreshedQuestions);
    const selectedStartQuestion = selectedImportableQuestions.find(
      (question) => question.id === selected.guide.startQuestionId,
    );
    const selectedStartIndex = selectedStartQuestion
      ? selectedImportableQuestions.indexOf(selectedStartQuestion)
      : 0;
    const importedStartQuestionId = importedQuestions[selectedStartIndex]?.id;

    setGuide((prev) => {
      const existingQuestions = prev?.questions || [];
      const anchorQuestions = existingQuestions.filter((question) => question.isAnchor);
      const nonAnchorQuestions = existingQuestions.filter((question) => !question.isAnchor);
      const replaceMode = importMode === "replace";
      const keepQuestions = replaceMode
        ? nonAnchorQuestions.filter(isCannedQuestion)
        : nonAnchorQuestions;
      const existingBankIds = new Set(
        keepQuestions.map((question) => String(question.bankQuestionId || "")),
      );
      const uniqueImports = importedQuestions.filter((question) => {
        if (!isCannedQuestion(question)) return true;
        const bankId = String(question.bankQuestionId);
        if (existingBankIds.has(bankId)) return false;
        existingBankIds.add(bankId);
        return true;
      });
      const mergedQuestions = sanitizeQuestions([
        ...anchorQuestions,
        ...keepQuestions,
        ...uniqueImports,
      ]);
      const startQuestionId = mergedQuestions.some(
        (question) => question.id === importedStartQuestionId,
      )
        ? importedStartQuestionId
        : mergedQuestions.find(
            (question) =>
              selectedStartQuestion?.bankQuestionId &&
              String(question.bankQuestionId) === String(selectedStartQuestion.bankQuestionId),
          )?.id || keepQuestions[0]?.id;

      const nextStartQuestionId = replaceMode
        ? resolveStartQuestionId(startQuestionId, mergedQuestions)
        : resolveStartQuestionId(prev?.startQuestionId || importedStartQuestionId, mergedQuestions);

      return {
        ...(prev || createDefaultGuide(campaign)),
        questions: mergedQuestions,
        startQuestionId: nextStartQuestionId,
      };
    });

    setSaveState("idle");
    setSaveFeedback(
      importMode === "replace"
        ? `Replaced with questions from ${selected.code}; existing canned questions were kept.`
        : `Added questions from ${selected.code}; duplicate canned questions were skipped.`,
    );
    setActiveQuestionId((prev) => prev || importedQuestions[0]?.id || null);
    setImportSubmitting(false);
    closeImportModal(true);
  };

  const questionCount = guide?.questions?.length || 0;
  const authoredQuestionCount = (guide?.questions || []).filter(
    (question) => !question.isAnchor,
  ).length;
  const hasUploadingMedia = (guide?.questions || []).some((question) => question.mediaUploading);
  const hasUnsavedChanges = saveState === "idle" || saveState === "error";
  const sectionsCount = useMemo(() => {
    const sections = new Set((guide?.questions || []).map((question) => question.section.trim()));
    return sections.size;
  }, [guide]);
  const routingCount = useMemo(
    () =>
      (guide?.questions || []).reduce((sum, question) => {
        if (usesOptionRouting(question.answerType)) {
          return (
            sum +
            question.options.filter((option) => option.routeTo && option.routeTo !== ROUTE_END)
              .length
          );
        }
        return sum + (question.routeTo && question.routeTo !== ROUTE_END ? 1 : 0);
      }, 0),
    [guide],
  );

  const routeOptions = useMemo(() => {
    const base = (guide?.questions || []).map((question, index) => ({
      value: question.id,
      label: `Q${index + 1} · ${String(question.code || `q${index + 1}`).toUpperCase()}: ${truncateText(question.prompt, 42)}`,
    }));

    return [
      { value: ROUTE_CONTINUE, label: "Continue (next question)" },
      { value: ROUTE_END, label: "End survey" },
      ...base,
    ];
  }, [guide]);

  const filteredQuestionBank = useMemo(() => {
    const query = questionBankSearch.trim().toLowerCase();
    if (!query) return questionBankQuestions;
    return questionBankQuestions.filter((question) =>
      [question?.code, question?.prompt, ...(question?.options || [])]
        .map((value) => (typeof value === "string" ? value : value?.label || ""))
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [questionBankQuestions, questionBankSearch]);

  const validation = useMemo(() => {
    const questions = guide?.questions || [];
    const missingPromptIds = [];
    const missingOptionLabelsByQuestion = {};
    const invalidRankingItemCountIds = [];
    const unroutedCountByQuestion = {};
    let unroutedCount = 0;

    questions.forEach((question) => {
      const questionUnroutedCount = usesOptionRouting(question.answerType)
        ? question.options.filter((option) => option.needsRoute).length
        : question.needsRoute
          ? 1
          : 0;
      if (questionUnroutedCount) {
        unroutedCountByQuestion[question.id] = questionUnroutedCount;
        unroutedCount += questionUnroutedCount;
      }

      if (!question.prompt.trim()) {
        missingPromptIds.push(question.id);
      }

      if (getAnswerTypeMeta(question.answerType).supportsOptions) {
        const missingLabels = question.options.filter((option) => !option.label.trim()).length;
        if (missingLabels) {
          missingOptionLabelsByQuestion[question.id] = missingLabels;
        }

        if (isRankingAnswerType(question.answerType) && question.options.length < 2) {
          invalidRankingItemCountIds.push(question.id);
        }
      }
    });

    const hasStart = Boolean(
      guide?.startQuestionId && questions.some((q) => q.id === guide.startQuestionId),
    );
    const pathsReachEnd = allPathsReachEnd(questions, guide?.startQuestionId);

    return {
      hasStart,
      pathsReachEnd,
      missingPromptIds,
      missingOptionLabelsByQuestion,
      unroutedCount,
      perQuestion: questions.reduce((accumulator, question) => {
        const issues = [];

        if (missingPromptIds.includes(question.id)) {
          issues.push("Question text is required.");
        }

        if (missingOptionLabelsByQuestion[question.id]) {
          issues.push(
            `${missingOptionLabelsByQuestion[question.id]} choice option${missingOptionLabelsByQuestion[question.id] === 1 ? " is" : "s are"} missing a label.`,
          );
        }

        if (invalidRankingItemCountIds.includes(question.id)) {
          issues.push("Ranking questions need at least 2 items to rank.");
        }
        if (unroutedCountByQuestion[question.id]) {
          issues.push(
            `${unroutedCountByQuestion[question.id]} route${unroutedCountByQuestion[question.id] === 1 ? " is" : "s are"} not set.`,
          );
        }

        accumulator[question.id] = issues;
        return accumulator;
      }, {}),
      global: [
        !hasStart && questions.length ? "Select one question as the survey start." : null,
        missingPromptIds.length
          ? `${missingPromptIds.length} question${missingPromptIds.length === 1 ? " is" : "s are"} missing text.`
          : null,
        Object.keys(missingOptionLabelsByQuestion).length
          ? "Every choice option or ranking item needs a label before launch."
          : null,
        invalidRankingItemCountIds.length
          ? `${invalidRankingItemCountIds.length} ranking question${invalidRankingItemCountIds.length === 1 ? " requires" : "s require"} at least 2 items.`
          : null,
        unroutedCount
          ? `${unroutedCount} canned question route${unroutedCount === 1 ? " needs" : "s need"} fixing before this guide can be saved.`
          : null,
        questions.length && !pathsReachEnd
          ? "Some routes do not currently reach End survey."
          : null,
      ].filter(Boolean),
    };
  }, [guide]);

  const readinessChecks = useMemo(() => {
    return [
      {
        label: "Survey is ready to save",
        passed:
          questionCount > 0 &&
          !validation.missingPromptIds.length &&
          !Object.keys(validation.missingOptionLabelsByQuestion).length &&
          !validation.unroutedCount,
      },
      { label: "Start question set", passed: validation.hasStart },
      { label: "Every path can reach the end", passed: validation.pathsReachEnd },
    ];
  }, [questionCount, validation]);

  const flowGraph = useMemo(() => buildSurveyFlowGraph(guide?.questions || []), [guide]);

  const startMapNode = useMemo(
    () => flowGraph.nodes.find((node) => node.id === guide?.startQuestionId) || flowGraph.nodes[0],
    [flowGraph, guide],
  );

  useEffect(() => {
    if (!pendingQuestionScrollIdRef.current) return;

    const questionId = pendingQuestionScrollIdRef.current;
    pendingQuestionScrollIdRef.current = null;

    window.requestAnimationFrame(() => {
      const target = questionRefs.current[questionId];
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [guide?.questions]);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!hasUnsavedChanges) return undefined;
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const fitExpandedMap = () => {
    if (!expandedMapViewportRef.current) return;
    const nextScale = getMapFitScale(flowGraph, expandedMapViewportRef.current, 1);
    setExpandedMapScale(nextScale);

    window.requestAnimationFrame(() => {
      if (!expandedMapViewportRef.current) return;

      const centerScroll = getViewportCenterScroll(
        flowGraph,
        expandedMapViewportRef.current,
        nextScale,
      );

      expandedMapViewportRef.current.scrollTo({
        left: centerScroll.left,
        top: centerScroll.top,
        behavior: "smooth",
      });
    });
  };

  useEffect(() => {
    const syncInlineMapScale = () => {
      if (!inlineMapViewportRef.current) return;

      const nextScale = getMapFitScale(flowGraph, inlineMapViewportRef.current, 0.82);
      setInlineMapScale(nextScale);

      window.requestAnimationFrame(() => {
        if (!inlineMapViewportRef.current) return;

        const centerScroll = getViewportCenterScroll(
          flowGraph,
          inlineMapViewportRef.current,
          nextScale,
        );

        inlineMapViewportRef.current.scrollTo({
          left: centerScroll.left,
          top: centerScroll.top,
          behavior: "auto",
        });
      });
    };

    syncInlineMapScale();

    if (typeof ResizeObserver === "undefined" || !inlineMapViewportRef.current) return undefined;

    const observer = new ResizeObserver(() => syncInlineMapScale());
    observer.observe(inlineMapViewportRef.current);

    return () => observer.disconnect();
  }, [flowGraph]);

  useEffect(() => {
    if (!mapExpanded) return undefined;

    const animationFrame = window.requestAnimationFrame(() => {
      if (!expandedMapViewportRef.current) return;

      const nextScale = getMapFitScale(flowGraph, expandedMapViewportRef.current, 1);
      setExpandedMapScale(nextScale);

      window.requestAnimationFrame(() => {
        if (!expandedMapViewportRef.current) return;

        const centerScroll = getViewportCenterScroll(
          flowGraph,
          expandedMapViewportRef.current,
          nextScale,
        );

        expandedMapViewportRef.current.scrollTo({
          left: centerScroll.left,
          top: centerScroll.top,
          behavior: "smooth",
        });
      });
    });
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setMapExpanded(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [mapExpanded, flowGraph]);

  const scrollToQuestion = (questionId) => {
    const target = questionRefs.current[questionId];
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const onMapNodeSelect = (questionId) => {
    setActiveQuestionId(questionId);

    if (mapExpanded) {
      setMapExpanded(false);
      window.requestAnimationFrame(() => scrollToQuestion(questionId));
      return;
    }

    scrollToQuestion(questionId);
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>Loading survey builder…</div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          <h1>Survey builder unavailable</h1>
          <p>{error || "The requested campaign could not be loaded."}</p>
          <button
            type="button"
            className={styles.saveButton}
            onClick={() => history.push(Path.Campaigns)}
          >
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  if (campaign.surveyType !== "custom") {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          <h1>Survey builder is only available for custom surveys</h1>
          <p>
            {campaign.title} is configured as a simple survey. Switch the campaign to a custom
            survey before editing a guided questionnaire.
          </p>
          <button type="button" className={styles.saveButton} onClick={goBackToCampaign}>
            Back to Campaign
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.breadcrumbs}>
        <button
          type="button"
          className={styles.breadcrumbLink}
          onClick={() => history.push(Path.Campaigns)}
        >
          My Campaigns
        </button>
        <span className={styles.breadcrumbSep}>›</span>
        <button type="button" className={styles.breadcrumbLink} onClick={goBackToCampaign}>
          {campaign.code}
        </button>
        <span className={styles.breadcrumbSep}>›</span>
        <span className={styles.breadcrumbCurrent}>Survey Builder</span>
      </div>

      <section className={styles.builderHeader}>
        <div className={styles.builderIdentity}>
          <div className={styles.campaignChip}>{campaign.code}</div>
          <div>
            <h1>{campaign.title}</h1>
            <p>Survey Builder</p>
          </div>
        </div>

        <div className={styles.builderActions}>
          <div className={styles.saveIndicator}>
            <span className={styles.saveDot} />
            {saveState === "saving"
              ? "Saving survey guide…"
              : saveState === "saved"
                ? "All changes saved"
                : saveState === "error"
                  ? "Save failed"
                  : "Unsaved changes"}
          </div>
          <button type="button" className={styles.backButton} onClick={goBackToCampaign}>
            Back
          </button>
          <button
            type="button"
            className={styles.exportButton}
            onClick={onExportGuide}
            disabled={!guide || hasUploadingMedia}
          >
            Export Guide
          </button>
          <button
            type="button"
            className={`${styles.saveButton} ${saveState === "saving" ? styles.saveButtonLoading : ""}`}
            onClick={onSaveGuide}
            disabled={saveState === "saving" || hasUploadingMedia}
          >
            {saveState === "saving" || hasUploadingMedia ? (
              <>
                <span className={styles.buttonSpinner} aria-hidden="true" />
                {saveState === "saving" ? "Saving Survey Guide..." : "Uploading media..."}
              </>
            ) : (
              "Save Survey Guide"
            )}
          </button>
        </div>
      </section>

      {saveFeedback ? <div className={styles.builderMessage}>{saveFeedback}</div> : null}

      {anchorMigrationNotices.length ? (
        <div className={styles.builderMessage}>
          {anchorMigrationNotices.map((notice) => (
            <div key={notice}>{notice}</div>
          ))}
        </div>
      ) : null}

      {validation.global.length ? (
        <section className={styles.validationPanel}>
          <h2>Validation feedback</h2>
          <ul className={styles.validationList}>
            {validation.global.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className={styles.builderLayout}>
        <section className={styles.canvasPanel}>
          <section className={styles.standardQuestionsPanel}>
            <div className={styles.standardQuestionsHeader}>Anchor Questions</div>
            <div className={styles.standardQuestionsChips}>
              {STANDARD_ANCHOR_QUESTIONS.map((question, index) => (
                <span key={question.key} className={styles.standardQuestionChip}>
                  <span className={styles.standardQuestionChipIndex}>{index + 1}</span>
                  <span className={styles.standardQuestionChipText}>{question.prompt}</span>
                </span>
              ))}
            </div>
            <p className={styles.standardQuestionsMeta}>
              These are seeded automatically with fixed IDs and locked wording so results stay
              comparable across campaigns. Routing and placement remain editable.
            </p>
          </section>

          <div className={styles.canvasTopbar}>
            <div className={styles.canvasCount}>{authoredQuestionCount} questions</div>
            <div className={styles.canvasMeta}>
              <span>{sectionsCount} sections</span>
              <span>{routingCount} routing links</span>
            </div>
            <button type="button" className={styles.importButton} onClick={openImportModal}>
              Import Questions
            </button>
            <button
              type="button"
              className={styles.questionBankButton}
              onClick={() => openQuestionBankModal()}
            >
              Question Bank
            </button>
          </div>

          <div className={styles.questionStack}>
            {!authoredQuestionCount ? (
              <div className={styles.emptyQuestionState}>
                <strong>Add your first campaign question</strong>
                <span>
                  Anchor questions are ready. Add campaign-specific questions or import from a
                  previous campaign.
                </span>
                <button
                  type="button"
                  className={styles.emptyStateImportButton}
                  onClick={openImportModal}
                >
                  Import Questions from a previous campaign
                </button>
              </div>
            ) : null}

            {guide?.questions?.map((question, index) => {
              const answerTypeMeta = getAnswerTypeMeta(question.answerType);
              const isRanking = isRankingAnswerType(question.answerType);
              const rankingItemCount = getRankingItemCount(question);
              const rankingCount = isRanking
                ? getResolvedRankCount(question, question.options)
                : null;
              const rankingRemainderLabel =
                rankingCount === rankingItemCount
                  ? `of ${rankingItemCount} · all`
                  : `of ${rankingItemCount}`;
              const isCanned = isCannedQuestion(question);
              const hasUnroutedPaths =
                Boolean(question.needsRoute) ||
                question.options.some((option) => option.needsRoute);
              const defaultRoute =
                routeOptions.find((option) => option.value === question.routeTo)?.label ||
                "End Survey";

              const inserterOpen = openInserterIndex === index;

              return (
                <React.Fragment key={question.id}>
                  {index > 0 ? (
                    <div
                      className={`${styles.questionInserter} ${
                        inserterOpen ? styles.questionInserterOpen : ""
                      }`}
                    >
                      <button
                        type="button"
                        className={styles.questionInserterToggle}
                        aria-label={
                          inserterOpen
                            ? "Close insert options"
                            : `Insert a question before question ${index + 1}`
                        }
                        aria-expanded={inserterOpen}
                        onClick={() => setOpenInserterIndex(inserterOpen ? null : index)}
                      >
                        <span className={styles.questionInserterPlus} aria-hidden="true">
                          +
                        </span>
                      </button>
                      {inserterOpen ? (
                        <div className={styles.questionInserterPanel}>
                          <button
                            type="button"
                            className={styles.questionInserterOption}
                            onClick={() => addQuestion(index)}
                          >
                            + Add custom question
                          </button>
                          <button
                            type="button"
                            className={styles.questionInserterOption}
                            onClick={() => openQuestionBankModal(index)}
                          >
                            Add from Question Bank
                          </button>
                          <button
                            type="button"
                            className={styles.questionInserterClose}
                            aria-label="Close insert options"
                            onClick={() => setOpenInserterIndex(null)}
                          >
                            ×
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <article
                    ref={(node) => {
                      if (node) {
                        questionRefs.current[question.id] = node;
                      } else {
                        delete questionRefs.current[question.id];
                      }
                    }}
                    className={`${styles.questionCard} ${
                      activeQuestionId === question.id ? styles.questionCardActive : ""
                    } ${hasUnroutedPaths ? styles.questionCardNeedsRoute : ""}`}
                    onClick={() => setActiveQuestionId(question.id)}
                  >
                    <div className={styles.questionHeader}>
                      <div className={styles.questionHeaderLeft}>
                        <div className={styles.questionNumber}>{index + 1}</div>
                        <div className={styles.questionHeaderMeta}>
                          <div className={styles.questionCodeRow}>
                            <span className={styles.questionCode}>
                              {question.bankQuestionCode || question.code}
                            </span>
                            {question.isAnchor ? (
                              <span className={styles.anchorBadge}>
                                <span className={styles.anchorBadgeIcon} aria-hidden="true">
                                  <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <rect x="5" y="11" width="14" height="9" rx="2" />
                                    <path d="M8 11V8a4 4 0 1 1 8 0v3" />
                                  </svg>
                                </span>
                                Anchor
                              </span>
                            ) : isCanned ? (
                              <span className={styles.cannedBadge}>Canned</span>
                            ) : (
                              <span className={styles.customBadge}>Custom</span>
                            )}
                            {guide?.startQuestionId === question.id ? (
                              <span className={styles.startBadge}>Start</span>
                            ) : null}
                            {question.isAnchor ? (
                              <span className={styles.anchorLockHint}>{ANCHOR_LOCK_HINT}</span>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {isCanned ? (
                        <button
                          type="button"
                          className={styles.questionBankEditLink}
                          onClick={(event) => {
                            event.stopPropagation();
                            navigateToQuestionBank(
                              Path.getQuestionBankQuestion(question.bankQuestionId),
                            );
                          }}
                        >
                          Edit in Question Bank
                        </button>
                      ) : null}

                      <div className={styles.questionControls}>
                        <button
                          type="button"
                          className={
                            guide?.startQuestionId === question.id
                              ? `${styles.iconButton} ${styles.startControlActive}`
                              : styles.iconButton
                          }
                          onClick={() => setStartQuestion(question.id)}
                          aria-label="Set start question"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            className={styles.startIcon}
                            aria-hidden="true"
                            fill="currentColor"
                          >
                            <polygon points="8 5 19 12 8 19 8 5" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className={styles.iconButton}
                          onClick={() => moveQuestion(question.id, -1)}
                          disabled={index === 0}
                          aria-label="Move question up"
                        >
                          <span className={styles.moveButtonLabel} aria-hidden="true">
                            ↑
                          </span>
                        </button>
                        <button
                          type="button"
                          className={styles.iconButton}
                          onClick={() => moveQuestion(question.id, 1)}
                          disabled={index === guide.questions.length - 1}
                          aria-label="Move question down"
                        >
                          <span className={styles.moveButtonLabel} aria-hidden="true">
                            ↓
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                          onClick={() => removeQuestion(question.id)}
                          disabled={question.isAnchor}
                          title={question.isAnchor ? ANCHOR_DELETE_BLOCK_REASON : "Delete question"}
                          aria-label="Delete question"
                        >
                          ✕
                        </button>
                      </div>
                    </div>

                    {hasUnroutedPaths ? (
                      <div className={styles.needsRouteMessage}>
                        This question was changed in the Question Bank. Set a route for each
                        highlighted option before saving.
                      </div>
                    ) : null}

                    <div className={styles.questionBody}>
                      <div className={styles.fieldBlock}>
                        <label className={styles.microLabel}>Section</label>
                        <input
                          className={`${styles.fieldInput} ${question.isAnchor ? styles.readOnlyInput : ""}`}
                          value={question.section}
                          onChange={(event) =>
                            onQuestionChange(question.id, "section", event.target.value)
                          }
                          readOnly={question.isAnchor}
                        />
                      </div>

                      <div className={styles.fieldBlock}>
                        <label className={styles.microLabel}>Question</label>
                        <textarea
                          className={`${styles.questionTextarea} ${question.isAnchor || isCanned ? styles.readOnlyInput : ""}`}
                          value={question.prompt}
                          onChange={(event) =>
                            onQuestionChange(question.id, "prompt", event.target.value)
                          }
                          readOnly={question.isAnchor || isCanned}
                        />
                      </div>

                      <div className={styles.fieldBlock}>
                        <label className={styles.microLabel}>Media</label>
                        {question.isAnchor || isCanned ? (
                          <div className={styles.mediaHint}>
                            {question.isAnchor
                              ? "Media attachments are disabled for anchor questions."
                              : "Media attachments are disabled for canned questions."}
                          </div>
                        ) : null}
                        <div
                          className={`${styles.mediaPanel} ${
                            dragOverQuestionId === question.id ? styles.mediaPanelDragOver : ""
                          }`}
                          onDragOver={(event) =>
                            onMediaPanelDragOver(
                              event,
                              question.id,
                              Boolean(question.mediaUploading),
                            )
                          }
                          onDragLeave={(event) => onMediaPanelDragLeave(event, question.id)}
                          onDrop={(event) =>
                            onMediaPanelDrop(event, question.id, Boolean(question.mediaUploading))
                          }
                        >
                          {question.mediaType === "image" && question.mediaUrl ? (
                            <img
                              src={question.mediaUrl}
                              alt={question.mediaName || "Attached media"}
                              className={styles.mediaPreviewImage}
                            />
                          ) : null}

                          {question.mediaType === "video" && question.mediaUrl ? (
                            <video className={styles.mediaPreviewVideo} controls preload="metadata">
                              <source src={question.mediaUrl} />
                            </video>
                          ) : null}

                          {!question.mediaKey && !question.mediaUploading ? (
                            <div className={styles.mediaEmptyState}>
                              No media attached. Drop an image or video here, or use the attach
                              button.
                            </div>
                          ) : null}

                          {question.mediaKey ? (
                            <div className={styles.mediaMetaRow}>
                              <span className={styles.mediaMetaType}>
                                {question.mediaType || "media"}
                              </span>
                              <span className={styles.mediaMetaName}>
                                {question.mediaName || getMediaFilenameFromKey(question.mediaKey)}
                              </span>
                              {!question.mediaUrl ? (
                                <span className={styles.mediaMetaHint}>
                                  Preview available after save.
                                </span>
                              ) : null}
                            </div>
                          ) : null}

                          <div className={styles.mediaActionRow}>
                            <label
                              className={
                                question.mediaUploading || question.isAnchor || isCanned
                                  ? `${styles.mediaAttachButton} ${styles.mediaAttachButtonDisabled}`
                                  : styles.mediaAttachButton
                              }
                            >
                              <input
                                type="file"
                                className={styles.mediaFileInput}
                                accept={MEDIA_FILE_ACCEPT}
                                disabled={
                                  Boolean(question.mediaUploading) || question.isAnchor || isCanned
                                }
                                onChange={(event) =>
                                  uploadQuestionMedia({
                                    questionId: question.id,
                                    file: event.target.files?.[0],
                                    inputElement: event.target,
                                  })
                                }
                              />
                              {question.mediaUploading
                                ? "Uploading media..."
                                : question.mediaKey
                                  ? "Replace media"
                                  : "Attach media"}
                            </label>

                            {question.mediaKey ? (
                              <button
                                type="button"
                                className={styles.mediaDetachButton}
                                disabled={
                                  Boolean(question.mediaUploading) || question.isAnchor || isCanned
                                }
                                onClick={() => detachQuestionMedia(question.id)}
                              >
                                Detach
                              </button>
                            ) : null}
                          </div>
                        </div>

                        <div className={styles.mediaHint}>
                          Supported: jpg, jpeg, png, gif, mp4, mov, avi. You can also drag and drop
                          an image or video into the media area above.
                        </div>

                        {question.mediaUploadError ? (
                          <div className={styles.questionValidationItem}>
                            {question.mediaUploadError}
                          </div>
                        ) : null}
                      </div>

                      <div className={styles.answerTypeBlock}>
                        <div className={styles.microLabel}>Answer Type</div>
                        <div className={styles.answerTypeGrid}>
                          {ANSWER_TYPES.map((type) => (
                            <div key={type.value} className={styles.answerTypeOptionGroup}>
                              <button
                                type="button"
                                className={
                                  question.answerType === type.value
                                    ? `${styles.answerTypeButton} ${styles.answerTypeButtonActive}`
                                    : styles.answerTypeButton
                                }
                                onClick={() => onQuestionTypeChange(question.id, type.value)}
                                disabled={question.isAnchor || isCanned}
                              >
                                {type.label}
                              </button>

                              {type.value === "multipleChoice" &&
                              question.answerType === "multipleChoice" ? (
                                <button
                                  type="button"
                                  className={styles.answerTypeTemplateButton}
                                  onClick={() => applyYesNoTemplate(question.id)}
                                  disabled={question.isAnchor || isCanned}
                                >
                                  Yes / No
                                </button>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>

                      {answerTypeMeta.supportsOptions ? (
                        <>
                          <div className={styles.fieldBlock}>
                            <div className={styles.microLabel}>
                              {isRanking ? "Items to rank" : "Options & Routing"}
                            </div>
                            <div className={styles.optionList}>
                              {question.options.map((option) => (
                                <div key={option.id} className={styles.optionBlock}>
                                  <div
                                    className={
                                      isRanking
                                        ? `${styles.optionRow} ${styles.optionRowRanking}`
                                        : styles.optionRow
                                    }
                                  >
                                    <input
                                      className={`${styles.fieldInput} ${question.isAnchor || isCanned ? styles.readOnlyInput : ""}`}
                                      value={option.label}
                                      onChange={(event) =>
                                        updateQuestionOption(
                                          question.id,
                                          option.id,
                                          "label",
                                          event.target.value,
                                        )
                                      }
                                      readOnly={question.isAnchor || isCanned}
                                    />
                                    {!isRanking ? (
                                      <div className={styles.optionArrow}>→</div>
                                    ) : null}
                                    {!isRanking ? (
                                      <select
                                        className={`${styles.routeSelect} ${option.needsRoute ? styles.routeSelectNeedsRoute : ""}`}
                                        value={option.needsRoute ? "" : option.routeTo}
                                        onChange={(event) =>
                                          updateQuestionOption(
                                            question.id,
                                            option.id,
                                            "routeTo",
                                            event.target.value,
                                          )
                                        }
                                      >
                                        {option.needsRoute ? (
                                          <option value="" disabled>
                                            ⚠ Choose a route…
                                          </option>
                                        ) : null}
                                        {routeOptions.map((routeOption) => (
                                          <option key={routeOption.value} value={routeOption.value}>
                                            {routeOption.label}
                                          </option>
                                        ))}
                                      </select>
                                    ) : null}
                                    <button
                                      type="button"
                                      className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                                      onClick={() => removeQuestionOption(question.id, option.id)}
                                      disabled={
                                        question.options.length <= 1 ||
                                        question.isAnchor ||
                                        isCanned
                                      }
                                      aria-label="Remove option"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                  <div className={styles.optionTagsRow}>
                                    <span className={styles.tagsLabel}>TAGS</span>
                                    {(option.cohorts || []).length === 0 && (
                                      <span className={styles.tagsNone}>none</span>
                                    )}
                                    {(option.cohorts || []).map((cohort) => (
                                      <span
                                        key={cohort.cohortId}
                                        className={styles.cohortTag}
                                        style={{ borderColor: cohort.color }}
                                      >
                                        <span
                                          className={styles.cohortTagDot}
                                          style={{ background: cohort.color }}
                                        />
                                        {cohort.name}
                                        <button
                                          type="button"
                                          className={styles.cohortTagRemove}
                                          onClick={() =>
                                            removeCohortFromOption(
                                              question.id,
                                              option.id,
                                              cohort.cohortId,
                                            )
                                          }
                                          aria-label={`Remove ${cohort.name}`}
                                        >
                                          ×
                                        </button>
                                      </span>
                                    ))}
                                    <CohortPickerTrigger
                                      isOpen={openCohortPickerOptionId === option.id}
                                      onToggle={() => {
                                        const opening = openCohortPickerOptionId !== option.id;
                                        if (opening) setCohortPickerSearch("");
                                        setOpenCohortPickerOptionId((prev) =>
                                          prev === option.id ? null : option.id,
                                        );
                                      }}
                                      onClose={() => setOpenCohortPickerOptionId(null)}
                                      groupedCohorts={groupedCohorts}
                                      search={cohortPickerSearch}
                                      onSearch={setCohortPickerSearch}
                                      onSelect={(cohort) =>
                                        addCohortToOption(question.id, option.id, cohort)
                                      }
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className={styles.fieldBlock}>
                            <button
                              type="button"
                              className={styles.addSmallButton}
                              onClick={() => addQuestionOption(question.id)}
                              disabled={question.isAnchor || isCanned}
                            >
                              {isRanking ? "+ Add item" : "+ Add option"}
                            </button>
                          </div>

                          {isRanking ? (
                            <>
                              <div className={styles.fieldBlock}>
                                <label className={styles.microLabel}>How many to rank</label>
                                <div className={styles.rankCountStepper}>
                                  <button
                                    type="button"
                                    className={styles.rankCountButton}
                                    onClick={() =>
                                      onQuestionChange(
                                        question.id,
                                        "rankCount",
                                        clamp(rankingCount - 1, 1, rankingItemCount),
                                      )
                                    }
                                    disabled={question.isAnchor || rankingCount <= 1}
                                    aria-label="Decrease rank count"
                                  >
                                    −
                                  </button>
                                  <input
                                    className={styles.rankCountInput}
                                    value={rankingCount}
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    onChange={(event) => {
                                      const digits = event.target.value.replace(/\D/g, "");
                                      if (!digits) {
                                        onQuestionChange(question.id, "rankCount", 1);
                                        return;
                                      }

                                      const nextValue = clamp(
                                        Number.parseInt(digits, 10),
                                        1,
                                        rankingItemCount,
                                      );
                                      onQuestionChange(question.id, "rankCount", nextValue);
                                    }}
                                    onBlur={(event) => {
                                      const digits = event.target.value.replace(/\D/g, "");
                                      const parsed = Number.parseInt(digits || "1", 10);
                                      onQuestionChange(
                                        question.id,
                                        "rankCount",
                                        clamp(parsed, 1, rankingItemCount),
                                      );
                                    }}
                                    disabled={question.isAnchor}
                                    aria-label="Rank count"
                                  />
                                  <button
                                    type="button"
                                    className={styles.rankCountButton}
                                    onClick={() =>
                                      onQuestionChange(
                                        question.id,
                                        "rankCount",
                                        clamp(rankingCount + 1, 1, rankingItemCount),
                                      )
                                    }
                                    disabled={question.isAnchor || rankingCount >= rankingItemCount}
                                    aria-label="Increase rank count"
                                  >
                                    +
                                  </button>
                                  <span className={styles.rankCountSuffix}>
                                    {rankingRemainderLabel}
                                  </span>
                                </div>
                                <div className={styles.routeHint}>
                                  In the app, enumerators tap options in order of preference.
                                </div>
                              </div>

                              <div className={styles.fieldBlock}>
                                <label className={styles.microLabel}>Then go to</label>
                                <select
                                  className={`${styles.routeSelect} ${question.needsRoute ? styles.routeSelectNeedsRoute : ""}`}
                                  value={question.needsRoute ? "" : question.routeTo}
                                  onChange={(event) =>
                                    onQuestionChange(question.id, "routeTo", event.target.value)
                                  }
                                >
                                  {question.needsRoute ? (
                                    <option value="" disabled>
                                      ⚠ Choose a route…
                                    </option>
                                  ) : null}
                                  {routeOptions.map((routeOption) => (
                                    <option key={routeOption.value} value={routeOption.value}>
                                      {routeOption.label}
                                    </option>
                                  ))}
                                </select>
                                <div className={styles.routeHint}>
                                  Current route: {defaultRoute}
                                </div>
                              </div>
                            </>
                          ) : null}
                        </>
                      ) : (
                        <div className={styles.fieldBlock}>
                          <label className={styles.microLabel}>Then go to</label>
                          <select
                            className={`${styles.routeSelect} ${question.needsRoute ? styles.routeSelectNeedsRoute : ""}`}
                            value={question.needsRoute ? "" : question.routeTo}
                            onChange={(event) =>
                              onQuestionChange(question.id, "routeTo", event.target.value)
                            }
                          >
                            {question.needsRoute ? (
                              <option value="" disabled>
                                ⚠ Choose a route…
                              </option>
                            ) : null}
                            {routeOptions.map((routeOption) => (
                              <option key={routeOption.value} value={routeOption.value}>
                                {routeOption.label}
                              </option>
                            ))}
                          </select>
                          <div className={styles.routeHint}>Current route: {defaultRoute}</div>
                        </div>
                      )}

                      <div className={styles.fieldBlock}>
                        <div className={styles.microLabel}>Interviewer Note</div>
                        {question.interviewerNotes[0] ? (
                          <div className={styles.qnoteBox}>
                            <span className={styles.qnoteIco}>
                              <svg viewBox="0 0 24 24" strokeWidth="2" fill="none">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="16" x2="12" y2="12" />
                                <line x1="12" y1="8" x2="12.01" y2="8" />
                              </svg>
                            </span>
                            <input
                              className={`${styles.fieldInput} ${question.isAnchor ? styles.readOnlyInput : ""}`}
                              data-noteid={question.interviewerNotes[0].id}
                              value={question.interviewerNotes[0].text}
                              placeholder="Instruction shown under the question (e.g. Huwag magbigay ng listahan...)"
                              onChange={(event) =>
                                updateInterviewerNote(
                                  question.id,
                                  question.interviewerNotes[0].id,
                                  event.target.value,
                                )
                              }
                              readOnly={question.isAnchor}
                            />
                            <button
                              type="button"
                              className={styles.qnoteRemove}
                              title="Remove note"
                              onClick={() =>
                                removeInterviewerNote(question.id, question.interviewerNotes[0].id)
                              }
                              aria-label="Remove interviewer note"
                              disabled={question.isAnchor}
                            >
                              <svg viewBox="0 0 24 24" strokeWidth="2" fill="none">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className={styles.addNoteButton}
                            onClick={() => addInterviewerNote(question.id)}
                            disabled={question.isAnchor}
                          >
                            + Add interviewer note
                          </button>
                        )}
                      </div>

                      {validation.perQuestion[question.id]?.length ? (
                        <div className={styles.questionValidation}>
                          {validation.perQuestion[question.id].map((issue) => (
                            <div key={issue} className={styles.questionValidationItem}>
                              {issue}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </article>
                </React.Fragment>
              );
            })}
          </div>

          <div className={styles.addQuestionActions}>
            <button
              type="button"
              className={styles.addQuestionButton}
              onClick={() => addQuestion()}
            >
              + Add Custom Question
            </button>
            <button
              type="button"
              className={styles.addQuestionButton}
              onClick={() => openQuestionBankModal()}
            >
              Add from Question Bank
            </button>
          </div>
          <button type="button" className={styles.resetButton} onClick={onResetGuide}>
            Reset Draft
          </button>
        </section>

        <aside className={styles.mapPanel}>
          <div className={styles.mapPanelHeader}>
            <div>
              <div className={styles.mapTitleRow}>
                <span className={styles.mapIcon}>◫</span>
                <h2>Question Map</h2>
              </div>
              <div className={styles.mapLegend}>
                <span>
                  <i className={`${styles.legendLine} ${styles.legendSequential}`} />
                  Sequential
                </span>
                <span>
                  <i className={`${styles.legendLine} ${styles.legendBranch}`} />
                  Branch / jump
                </span>
                <span>
                  <i className={`${styles.legendLine} ${styles.legendEnd}`} />
                  End survey
                </span>
              </div>
            </div>
            <button
              type="button"
              className={styles.expandButton}
              onClick={() => setMapExpanded(true)}
            >
              Expand
            </button>
          </div>

          <div ref={inlineMapViewportRef} className={styles.mapViewportRail}>
            {questionCount ? (
              <SurveyFlowCanvas
                graph={flowGraph}
                scale={inlineMapScale}
                variant="inline"
                showBranchLabels={false}
                selectedQuestionId={activeQuestionId}
                startQuestionId={startMapNode?.id}
                onNodeSelect={onMapNodeSelect}
              />
            ) : (
              <div className={styles.mapEmptyState}>
                <strong>Nothing to map yet</strong>
                <span>
                  The flow map appears here as soon as the survey has at least one question.
                </span>
              </div>
            )}
          </div>

          <div className={styles.mapRailHint}>
            Click a node to jump to that question in the editor.
          </div>

          <div className={styles.readinessList}>
            {readinessChecks.map((check) => (
              <div key={check.label} className={styles.readinessRow}>
                <span className={check.passed ? styles.readinessPassed : styles.readinessPending}>
                  {check.passed ? "✓" : "•"}
                </span>
                <span>{check.label}</span>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {mapExpanded ? (
        <div className={styles.mapModalBackdrop} role="presentation">
          <section
            className={styles.mapModal}
            role="dialog"
            aria-modal="true"
            aria-label="Survey flow map"
          >
            <div className={styles.mapModalHeader}>
              <div>
                <div className={styles.mapTitleRow}>
                  <span className={styles.mapIcon}>◫</span>
                  <h2>Survey Flow Map</h2>
                </div>
                <div className={styles.mapLegend}>
                  <span>
                    <i className={`${styles.legendLine} ${styles.legendSequential}`} />
                    Sequential
                  </span>
                  <span>
                    <i className={`${styles.legendLine} ${styles.legendBranch}`} />
                    Branch / jump
                  </span>
                  <span>
                    <i className={`${styles.legendLine} ${styles.legendEnd}`} />
                    End survey
                  </span>
                </div>
              </div>

              <div className={styles.mapModalControls}>
                <button
                  type="button"
                  className={styles.expandButton}
                  onClick={() => setExpandedMapScale((prev) => clamp(prev - 0.1, 0.4, 1.6))}
                >
                  Zoom Out
                </button>
                <div className={styles.zoomReadout}>{Math.round(expandedMapScale * 100)}%</div>
                <button
                  type="button"
                  className={styles.expandButton}
                  onClick={() => setExpandedMapScale((prev) => clamp(prev + 0.1, 0.4, 1.6))}
                >
                  Zoom In
                </button>
                <button type="button" className={styles.expandButton} onClick={fitExpandedMap}>
                  Fit
                </button>
                <button
                  type="button"
                  className={styles.expandButton}
                  onClick={() => setMapExpanded(false)}
                >
                  Close
                </button>
              </div>
            </div>

            <div ref={expandedMapViewportRef} className={styles.mapViewportExpanded}>
              {questionCount ? (
                <SurveyFlowCanvas
                  graph={flowGraph}
                  scale={expandedMapScale}
                  variant="expanded"
                  showBranchLabels
                  selectedQuestionId={activeQuestionId}
                  startQuestionId={startMapNode?.id}
                  onNodeSelect={onMapNodeSelect}
                />
              ) : (
                <div className={styles.mapEmptyState}>
                  <strong>Nothing to map yet</strong>
                  <span>Add questions to render the full survey flow.</span>
                </div>
              )}
            </div>
          </section>
        </div>
      ) : null}

      {questionBankModalOpen ? (
        <div className={styles.importModalBackdrop} role="presentation">
          <section
            className={`${styles.importModal} ${styles.questionBankModal}`}
            role="dialog"
            aria-modal="true"
            aria-label="Add from Question Bank"
          >
            <div className={styles.importModalHeader}>
              <h2>Add from Question Bank</h2>
              <p>Bank wording, answer type and options are shared across campaigns.</p>
            </div>
            <div className={styles.questionBankModalBody}>
              <input
                className={styles.questionBankSearch}
                value={questionBankSearch}
                onChange={(event) => setQuestionBankSearch(event.target.value)}
                placeholder="Search questions or IDs…"
                aria-label="Search Question Bank"
              />
              {questionBankLoading ? (
                <div className={styles.importModalState}>Loading questions…</div>
              ) : questionBankError ? (
                <div className={styles.importModalError}>{questionBankError}</div>
              ) : filteredQuestionBank.length ? (
                <div className={styles.questionBankRows}>
                  {filteredQuestionBank.map((bankQuestion) => {
                    const alreadyAdded = (guide?.questions || []).some(
                      (question) =>
                        String(question.bankQuestionId) === String(bankQuestion.bankQuestionId),
                    );
                    const optionPreview = (bankQuestion.options || [])
                      .map((option) => (typeof option === "string" ? option : option?.label))
                      .filter(Boolean)
                      .join(" · ");
                    const typeLabel = getAnswerTypeMeta(
                      normalizeAnswerType(bankQuestion.type),
                    ).label;
                    return (
                      <article key={bankQuestion.bankQuestionId} className={styles.questionBankRow}>
                        <div className={styles.questionBankRowContent}>
                          <div className={styles.questionBankRowMeta}>
                            <strong>{bankQuestion.code}</strong>
                            <span>{typeLabel}</span>
                          </div>
                          <p>{bankQuestion.prompt}</p>
                          {optionPreview ? <small>{optionPreview}</small> : null}
                        </div>
                        <button
                          type="button"
                          className={
                            alreadyAdded ? styles.questionBankAdded : styles.questionBankAdd
                          }
                          disabled={alreadyAdded}
                          onClick={() => addQuestionFromBank(bankQuestion)}
                        >
                          {alreadyAdded ? "Added" : "Add"}
                        </button>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.importModalState}>No matching questions found.</div>
              )}
            </div>
            <div className={styles.questionBankModalFooter}>
              <button
                type="button"
                className={styles.questionBankOpenLink}
                onClick={() => navigateToQuestionBank(Path.QuestionBank)}
              >
                Open Question Bank ↗
              </button>
              <button
                type="button"
                className={styles.importSubmitButton}
                onClick={() => {
                  bankInsertIndexRef.current = null;
                  setQuestionBankModalOpen(false);
                }}
              >
                Done
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {importModalOpen ? (
        <div className={styles.importModalBackdrop} role="presentation">
          <section
            className={styles.importModal}
            role="dialog"
            aria-modal="true"
            aria-label="Copy question set"
          >
            <div className={styles.importModalHeader}>
              <h2>Copy question set</h2>
              <p>
                Pick a previous campaign to reuse its questions, routing and media. You can edit
                everything after copying.
              </p>
            </div>

            <div className={styles.importModalBody}>
              {loadingImportCampaigns ? (
                <div className={styles.importModalState}>Loading campaigns…</div>
              ) : importCampaigns.length ? (
                <div className={styles.importCampaignList}>
                  {importCampaigns.map((entry) => {
                    const selected = String(selectedImportCampaignId) === String(entry.campaignId);

                    return (
                      <button
                        key={entry.campaignId}
                        type="button"
                        className={
                          selected
                            ? `${styles.importCampaignRow} ${styles.importCampaignRowSelected}`
                            : styles.importCampaignRow
                        }
                        onClick={() => {
                          setSelectedImportCampaignId(entry.campaignId);
                          setImportError(null);
                        }}
                      >
                        <span className={styles.importCampaignRadio} aria-hidden="true" />
                        <div className={styles.importCampaignMeta}>
                          <div className={styles.importCampaignCode}>{entry.code}</div>
                          <div className={styles.importCampaignTitle}>{entry.title}</div>
                          <div className={styles.importCampaignSubline}>
                            {entry.location} · {entry.date}
                          </div>
                        </div>
                        <span className={styles.importCampaignCount}>{entry.questionCount} Q</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.importModalState}>
                  {importError || "No importable campaigns found."}
                </div>
              )}

              <div className={styles.importModeRow}>
                <button
                  type="button"
                  className={
                    importMode === "replace"
                      ? `${styles.importModeButton} ${styles.importModeButtonActive}`
                      : styles.importModeButton
                  }
                  onClick={() => setImportMode("replace")}
                >
                  <strong>Replace</strong>
                  <span>Discard current questions</span>
                </button>
                <button
                  type="button"
                  className={
                    importMode === "append"
                      ? `${styles.importModeButton} ${styles.importModeButtonActive}`
                      : styles.importModeButton
                  }
                  onClick={() => setImportMode("append")}
                >
                  <strong>Append</strong>
                  <span>Add after existing ones</span>
                </button>
              </div>

              {importError && importCampaigns.length ? (
                <div className={styles.importModalError}>{importError}</div>
              ) : null}
            </div>

            <div className={styles.importModalActions}>
              <button
                type="button"
                className={styles.importCancelButton}
                onClick={closeImportModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.importSubmitButton}
                onClick={onImportQuestions}
                disabled={loadingImportCampaigns || !importCampaigns.length || importSubmitting}
              >
                {importSubmitting ? "Copying..." : "Copy questions"}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
};

export default SurveyBuilderPage;
